import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { sbGet, sbPost, SB_URL, SB_KEY } from './App';
import './Chat.css';
import { exportContent } from './exportUtils';
import {
  ensureBackupToken, isBackupConnected, getBackupAccountEmail, disconnectBackup, clearBackupSyncCache,
  ensureBackupRootFolder, syncConversationToDrive, hashText, getBackupMap,
} from './driveBackup';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const API_KEY = 'rag-bridge';
const GENERIC_BRIDGE_URL = 'https://generic.wavecanon.com/v1/chat/completions';

const MODELS = [
  {
    id: 'llama',
    label: 'Nemotron 3 Super · 120B',
    sub: 'OpenRouter · Free',
    url: 'https://llama.wavecanon.com/v1/chat/completions',
    model: 'nvidia/nemotron-3-super-120b-a12b:free', // OpenRouter free slug (Llama 3.3 ':free' was retired 2026-10); id stays 'llama' = bridge port 8014 / user_api_keys model_id
    free: true, // always active: the bridge uses the Canjur house key when the account has none
    defaultEndpoint: 'https://openrouter.ai/api/v1',
  },
  {
    id: 'groq',
    label: 'Llama 3.3 · 70B',
    sub: 'Groq',
    url: 'https://bridge.wavecanon.com/v1/chat/completions',
    model: 'llama-3.3-70b-versatile',
    apiRequired: true, // bring your own Groq key (no shared key since V5.3)
    defaultEndpoint: 'https://api.groq.com/openai/v1',
  },
  {
    id: 'claude',
    label: 'Claude Sonnet',
    sub: 'Anthropic',
    url: 'https://claude.wavecanon.com/v1/chat/completions',
    model: 'claude-sonnet-4-6',
    apiRequired: true,
    defaultEndpoint: 'https://api.anthropic.com/v1',
  },
  {
    id: 'grok',
    label: 'Grok 4.3',
    sub: 'xAI',
    url: 'https://grok.wavecanon.com/v1/chat/completions',
    model: 'grok-4.3',
    apiRequired: true,
    defaultEndpoint: 'https://api.x.ai/v1',
  },
  {
    id: 'deepseek',
    label: 'DeepSeek R1',
    sub: 'DeepSeek',
    url: 'https://deepseek.wavecanon.com/v1/chat/completions',
    model: 'deepseek-reasoner',
    apiRequired: true,
    defaultEndpoint: 'https://api.deepseek.com/v1',
  },
  {
    id: 'gemini',
    label: 'Gemini 2.5 Pro',
    sub: 'Google',
    url: 'https://openrouter.wavecanon.com/v1/chat/completions',
    model: 'google/gemini-2.5-pro',
    apiRequired: true,
    defaultEndpoint: 'https://openrouter.ai/api/v1',
  },
  {
    id: 'opus',
    label: 'Claude Opus',
    sub: 'Anthropic',
    url: 'https://opus.wavecanon.com/v1/chat/completions',
    model: 'anthropic/claude-opus-4-8',
    apiRequired: true,
    defaultEndpoint: 'https://openrouter.ai/api/v1',
  },
  {
    id: 'mistral',
    label: 'Mistral Large',
    sub: 'Mistral',
    url: 'https://mistral.wavecanon.com/v1/chat/completions',
    model: 'mistralai/mistral-large',
    apiRequired: true,
    defaultEndpoint: 'https://api.mistral.ai/v1',
  },
  {
    id: 'perplexity',
    label: 'Sonar Pro',
    sub: 'Perplexity',
    url: 'https://perplexity.wavecanon.com/v1/chat/completions',
    model: 'sonar-pro',
    apiRequired: true,
    defaultEndpoint: 'https://api.perplexity.ai',
  },
  {
    id: 'gpt54',
    label: 'GPT-5.4',
    sub: 'OpenAI',
    url: 'https://meta.wavecanon.com/v1/chat/completions',
    model: 'gpt-5.4',
    apiRequired: true,
    defaultEndpoint: 'https://api.openai.com/v1',
  },
  {
    id: 'haiku',
    label: 'Claude Haiku',
    sub: 'Anthropic',
    url: 'https://haiku.wavecanon.com/v1/chat/completions',
    model: 'claude-haiku-4-5-20251001',
    apiRequired: true,
    defaultEndpoint: 'https://api.anthropic.com/v1',
  },
  {
    id: 'fable',
    label: 'Claude Fable',
    sub: 'Anthropic',
    url: 'https://fable.wavecanon.com/v1/chat/completions',
    model: 'claude-fable-5',
    apiRequired: true,
    defaultEndpoint: 'https://api.anthropic.com/v1',
  },
];

const HOTLINK_URLS = {
  'Anthropic':  'https://console.anthropic.com/settings/keys',
  'Groq':       'https://console.groq.com/keys',
  'DeepSeek':   'https://platform.deepseek.com/api_keys',
  'Google':     'https://aistudio.google.com/app/apikey',
  'xAI':        'https://console.x.ai/',
  'Mistral':    'https://console.mistral.ai/api-keys',
  'Perplexity': 'https://www.perplexity.ai/settings/api',
  'OpenAI':     'https://platform.openai.com/api-keys',
};

// Default utility model options
const UTILITY_MODELS = [
  { id: 'groq-llama', label: 'Llama 3.3 70B (Groq) · Free', model: 'llama-3.3-70b-versatile', endpoint: 'https://api.groq.com/openai/v1' },
  { id: 'gpt4o-mini', label: 'GPT-4o Mini (OpenAI)', model: 'gpt-4o-mini', endpoint: 'https://api.openai.com/v1' },
  { id: 'claude-haiku', label: 'Claude Haiku (Anthropic)', model: 'claude-haiku-4-5', endpoint: 'https://api.anthropic.com/v1' },
  { id: 'gemini-flash', label: 'Gemini Flash (OpenRouter)', model: 'google/gemini-flash-1.5', endpoint: 'https://openrouter.ai/api/v1' },
];

// ── Context window helper ──────────────────────────────────────────────────────
const CONTEXT_WINDOW = 10; // last 10 messages (5 exchanges) sent when window is active

function buildContextWindow(fullHistory, sessionCtx) {
  if (!sessionCtx || fullHistory.length <= CONTEXT_WINDOW) return fullHistory;
  return [
    { role: 'system', content: `Earlier session context:\n${sessionCtx}` },
    ...fullHistory.slice(-CONTEXT_WINDOW),
  ];
}

// ── Bridge streaming (shared by single-model and Council) ─────────────────────
// Bridges RC_V5_5+ send SSE keepalive comments every 15 s and a structured
// `canjur` event for every failure (provider error, blocked, empty, truncated,
// library search timeout), so the app never has to show a blank answer.
const STREAM_IDLE_MS = 240000;   // abort only after 4 minutes with no bytes at all

function splitManifest(text) {
  const t = text || '';
  const i = t.indexOf('**SOURCE MANIFEST**');
  if (i === -1) return [t, ''];
  const j = t.lastIndexOf('---', i);
  const k = j !== -1 && i - j < 8 ? j : i;
  return [t.slice(0, k).replace(/\s+$/, ''), t.slice(k)];
}

function insertBeforeManifest(text, addition) {
  if (!addition) return text;
  const [body, manifest] = splitManifest(text);
  return body + addition + (manifest ? '\n\n' + manifest : '');
}

function noticeMessage(n) {
  return String(n?.message || '').replace(/^\s*[⚠ℹ]️?\s*/, '').trim();
}

// Fetch a bridge and read its stream. Never throws except on a user abort
// (AbortError). Returns { text, notices, network } where notices is an array of
// { status, reason, message } and network is true when the bridge was unreachable.
async function fetchBridge(url, init, userSignal, onDelta, label) {
  const ctrl = new AbortController();
  let idle = false;
  let timer = null;
  const arm = () => { clearTimeout(timer); timer = setTimeout(() => { idle = true; ctrl.abort(); }, STREAM_IDLE_MS); };
  const onUserAbort = () => ctrl.abort();
  if (userSignal) {
    if (userSignal.aborted) ctrl.abort();
    else userSignal.addEventListener('abort', onUserAbort);
  }
  const acc = { text: '', notices: [], network: false };
  arm();
  try {
    const resp = await fetch(url, { ...init, signal: ctrl.signal });
    arm();
    if (!resp.ok) {
      let detail = '';
      try {
        const raw = await resp.text();
        try { const j = JSON.parse(raw); detail = j.message || j.detail || j.error?.message || j.error || ''; } catch { detail = raw; }
      } catch {}
      acc.notices.push({ status: 'error', reason: `http_${resp.status}`, message: `${label} could not answer: HTTP ${resp.status}${detail ? ` (${String(detail).slice(0, 200)})` : ''}.` });
      return acc;
    }
    const reader = resp.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    const handle = (raw) => {
      const l = raw.trim();
      if (!l.startsWith('data:')) return;           // keepalive comments and stray lines
      const d = l.slice(5).trim();
      if (!d || d === '[DONE]') return;
      let j;
      try { j = JSON.parse(d); } catch { return; }
      if (j.canjur) { acc.notices.push(j.canjur); return; }
      const delta = j.choices?.[0]?.delta?.content || '';
      if (delta) { acc.text += delta; if (onDelta) onDelta(acc.text); }
    };
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      arm();
      buf += dec.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop();
      lines.forEach(handle);
    }
    if (buf) handle(buf);
    return acc;
  } catch (e) {
    if (idle) {
      acc.notices.push({ status: 'timeout', reason: 'idle', message: `${label} timed out (no response for ${STREAM_IDLE_MS / 60000} minutes).` });
      return acc;
    }
    if (e.name === 'AbortError') throw e;
    acc.network = true;
    acc.notices.push({ status: 'error', reason: 'network', message: `${label} could not be reached (${e.message}).` });
    return acc;
  } finally {
    clearTimeout(timer);
    if (userSignal) userSignal.removeEventListener('abort', onUserAbort);
  }
}

// One readable line per problem, for answers that did arrive.
function noticesNote(notices) {
  const lines = (notices || []).filter(n => n && n.status !== 'error').map(n => `> ${n.status === 'info' ? 'ℹ' : '⚠'} ${noticeMessage(n)}`);
  return lines.length ? '\n\n' + lines.join('\n>\n') : '';
}

// The message shown instead of a blank answer.
function failureText(label, notices) {
  const reasons = (notices || []).map(noticeMessage).filter(Boolean);
  return `⚠ **${label} did not return an answer.**\n\n${reasons.length ? reasons.join('\n\n') : `${label} returned an empty answer.`}\n\nUse **Retry** below to send the same request again.`;
}

// ── Citation verification ──────────────────────────────────────────────────────
function extractCitations(text) {
  const patterns = [
    // Case citations: Smith v. Jones, 123 F.2d 456 (9th Cir. 1999)
    /[A-Z][A-Za-z\s&,.']+v\.\s+[A-Z][A-Za-z\s&,.']+,\s+\d+\s+[A-Za-z.\s]+\d+\s*\([^)]+\d{4}\)/g,
    // USC: 12 U.S.C. § 1813(l)
    /\d+\s+U\.S\.C\.?\s+§+\s+[\d\w().]+/g,
    // CFR: 12 C.F.R. § 229.2(k)
    /\d+\s+C\.F\.R\.?\s+§+\s+[\d\w().]+/g,
    // UCC: UCC § 3-104
    /UCC\s+§+\s+[\d\w().]+/g,
    // Am Jur
    /Am\.?\s*Jur\.?\s*2d[^§]*§+\s*[\d\w().]+/g,
    // CJS
    /C\.J\.S\.[^§]*§+\s*[\d\w().]+/g,
    // Stat: 48 Stat. 1
    /\d+\s+Stat\.\s+\d+/g,
  ];
  const found = new Set();
  patterns.forEach(p => {
    const matches = text.match(p) || [];
    matches.forEach(m => found.add(m.trim()));
  });
  return Array.from(found);
}

// Jurisdiction detection from response text
function detectJurisdictions(text) {
  const jurisdictions = [];
  const federal = /federal|U\.S\.|United States|Congress|Supreme Court|circuit/i;
  const patterns = [
    { re: /First Circuit/i,    label: '1st Cir.' },
    { re: /Second Circuit/i,   label: '2nd Cir.' },
    { re: /Third Circuit/i,    label: '3rd Cir.' },
    { re: /Fourth Circuit/i,   label: '4th Cir.' },
    { re: /Fifth Circuit/i,    label: '5th Cir.' },
    { re: /Sixth Circuit/i,    label: '6th Cir.' },
    { re: /Seventh Circuit/i,  label: '7th Cir.' },
    { re: /Eighth Circuit/i,   label: '8th Cir.' },
    { re: /Ninth Circuit/i,    label: '9th Cir.' },
    { re: /Tenth Circuit/i,    label: '10th Cir.' },
    { re: /Eleventh Circuit/i, label: '11th Cir.' },
    { re: /D\.C\. Circuit/i,   label: 'D.C. Cir.' },
    { re: /New York/i,         label: 'N.Y.' },
    { re: /California/i,       label: 'Cal.' },
    { re: /Texas/i,            label: 'Tex.' },
    { re: /Florida/i,          label: 'Fla.' },
    { re: /Illinois/i,         label: 'Ill.' },
    { re: /Massachusetts/i,    label: 'Mass.' },
    { re: /Pennsylvania/i,     label: 'Pa.' },
  ];
  if (federal.test(text)) jurisdictions.push('Federal');
  patterns.forEach(({ re, label }) => {
    if (re.test(text)) jurisdictions.push(label);
  });
  return [...new Set(jurisdictions)].slice(0, 4);
}

// ── Parse source manifest from response text ───────────────────────────────────
// Extracts SOURCE MANIFEST block if present (from Source Mode)
function parseSourceManifest(text) {
  const manifestMatch = text.match(/\*\*SOURCE MANIFEST\*\*[\s\S]*?(?=\n---\n|\n\n\n|$)/i)
    || text.match(/SOURCE MANIFEST[\s\S]*?(?=\n---\n|\n\n\n|$)/i)
    || text.match(/VERIFIED SOURCES[\s\S]*?(?=\n---\n|\n\n\n|$)/i);
  if (!manifestMatch) return null;
  const lines = manifestMatch[0].split('\n').filter(l => l.trim().startsWith('-') || l.trim().startsWith('•'));
  const corpus = [];
  const live   = [];
  lines.forEach(line => {
    const clean = line
      .replace(/^[•-]\s*/, '')
      .replace(/\*\(score:[^)]+\)\*/gi, '')
      .replace(/score[:\s]+[\d.]+/gi, '')
      .replace(/\*/g, '')
      .trim();
    if (!clean || /^(Corpus|Live|Sources|---)/i.test(clean)) return;
    if (/CourtListener|Justia|Cornell|GovInfo|Scribd|Wikipedia/i.test(clean)) {
      live.push(clean);
    } else {
      corpus.push(clean);
    }
  });
  return (corpus.length || live.length) ? { corpus, live } : null;
}

// ── Build chat transcript text (pure -- used for export and Drive backup) ──
function buildChatTextFromRows(rows, sourceManifest, fallbackAssistantLabel) {
  const lines = [];
  (rows || []).forEach(m => {
    if (m.role === 'user') {
      lines.push('YOU\n' + m.content);
    } else if (m.role === 'assistant') {
      const label = m.modelLabel ? `${m.modelLabel} · ${m.modelSub}` : (fallbackAssistantLabel || 'Assistant');
      lines.push(label + '\n' + m.content);
    }
    lines.push('');
  });
  if (sourceManifest) {
    lines.push('\n── SOURCES ──');
    if (sourceManifest.corpus.length > 0) {
      lines.push('Corpus:');
      sourceManifest.corpus.forEach(s => lines.push('  ' + s));
    }
    if (sourceManifest.live.length > 0) {
      lines.push('Live:');
      sourceManifest.live.forEach(s => lines.push('  ' + s));
    }
  }
  return lines.join('\n');
}

// ── Build the full Drive-backup document: chat + sources + prompts + notes ─
function buildBackupMarkdown(chatRows, sourceManifest, promptTexts, noteRows) {
  const lines = [buildChatTextFromRows(chatRows, sourceManifest, 'Assistant')];
  if (promptTexts && promptTexts.length > 0) {
    lines.push('\n── PROMPTS ──');
    promptTexts.forEach((p, i) => lines.push(`${i + 1}. ${p}`));
  }
  if (noteRows && noteRows.length > 0) {
    lines.push('\n── NOTES ──');
    noteRows.forEach(n => lines.push(`- (message #${n.msg_index}) ${n.text}`));
  }
  return lines.join('\n');
}

// ── Prompt Item Component ─────────────────────────────────────────────────────
function PromptItem({ prompt, onUse }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied]     = useState(false);

  const title = prompt.length > 55 ? prompt.slice(0, 55) + '…' : prompt;

  function handleCopy(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className={`prompt-item ${expanded ? 'prompt-item--expanded' : ''}`}>
      <div className="prompt-item-header" onClick={() => setExpanded(!expanded)}>
        <span className="prompt-item-title">{title}</span>
        <div className="prompt-item-actions" onClick={e => e.stopPropagation()}>
          <button
            className={`prompt-copy-btn ${copied ? 'prompt-copy-btn--copied' : ''}`}
            onClick={handleCopy}
            title="Copy prompt"
          >
            {copied
              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            }
          </button>
          <span className="prompt-expand-icon" onClick={() => setExpanded(!expanded)}>▼</span>
        </div>
      </div>
      {expanded && (
        <div className="prompt-item-preview">
          {prompt}
          <br />
          <button className="prompt-use-btn" onClick={() => { onUse(); setExpanded(false); }}>
            Use this prompt
          </button>
        </div>
      )}
    </div>
  );
}

// ── Toast notification ────────────────────────────────────────────────────────
function Toast({ message, type }) {
  if (!message) return null;
  return (
    <div className={`toast toast--${type}`}>{message}</div>
  );
}

// ── Account Tab Component ─────────────────────────────────────────────────────
function AccountTab({ session }) {
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [pwStatus, setPwStatus] = React.useState('');
  const [savingPw, setSavingPw] = React.useState(false);

  const [newEmail, setNewEmail] = React.useState('');
  const [confirmEmail, setConfirmEmail] = React.useState('');
  const [emailStatus, setEmailStatus] = React.useState('');
  const [savingEmail, setSavingEmail] = React.useState(false);
  const [toast, setToast] = React.useState(null);

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  async function handleChangePassword() {
    if (!newPassword || newPassword !== confirmPassword) {
      setPwStatus('Passwords do not match.'); return;
    }
    if (newPassword.length < 6) {
      setPwStatus('Password must be at least 6 characters.'); return;
    }
    setSavingPw(true); setPwStatus('');
    try {
      const resp = await fetch(`${SB_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}` },
        body: JSON.stringify({ password: newPassword }),
      });
      if (resp.ok) {
        setPwStatus('Password updated.');
        setNewPassword(''); setConfirmPassword('');
      } else {
        const err = await resp.json();
        setPwStatus(err.message || 'Update failed. Please try again.');
      }
    } catch { setPwStatus('Network error. Please try again.'); }
    setSavingPw(false);
  }

  async function handleChangeEmail() {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || trimmed !== confirmEmail.trim().toLowerCase()) {
      setEmailStatus('Email addresses do not match.'); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailStatus('Please enter a valid email address.'); return;
    }
    if (trimmed === (session?.user?.email || '').toLowerCase()) {
      setEmailStatus('That is already your current email.'); return;
    }
    setSavingEmail(true); setEmailStatus('');
    try {
      const resp = await fetch(`${SB_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}` },
        body: JSON.stringify({ email: trimmed }),
      });
      if (resp.ok) {
        setEmailStatus('Confirmation link sent to ' + trimmed + '. Click it to complete the change.');
        showToast('Confirmation email sent to ' + trimmed);
        setNewEmail(''); setConfirmEmail('');
      } else {
        const err = await resp.json();
        const msg = err.message || 'Update failed. Please try again.';
        setEmailStatus(msg);
        showToast(msg, 'error');
      }
    } catch {
      setEmailStatus('Network error. Please try again.');
      showToast('Network error. Please try again.', 'error');
    }
    setSavingEmail(false);
  }

  return (
    <div className="api-keys-section">
      <label className="settings-section-label">Logged-in account</label>
      <p className="settings-note" style={{marginBottom:'0.75rem'}}>
        <strong>{session?.user?.email || 'Unknown'}</strong>
        <br /><span style={{fontSize:'0.65rem', opacity:0.6}}>User ID: {session?.user?.id}</span>
      </p>

      <label className="settings-section-label" style={{marginTop:'1rem'}}>Change Email</label>
      <label className="api-key-label">New Email Address</label>
      <input type="email" className="api-key-input" value={newEmail}
        onChange={e => setNewEmail(e.target.value)} placeholder="New email address" />
      <label className="api-key-label" style={{marginTop:'0.5rem'}}>Confirm New Email</label>
      <input type="email" className="api-key-input" value={confirmEmail}
        onChange={e => setConfirmEmail(e.target.value)} placeholder="Confirm new email address" />
      {emailStatus && (
        <p className="settings-note" style={{marginTop:'0.4rem', color: emailStatus.includes('sent') ? '#2e7d32' : '#c62828'}}>
          {emailStatus}
        </p>
      )}
      <button className="api-key-save-btn" style={{marginTop:'0.75rem'}} onClick={handleChangeEmail} disabled={savingEmail || !newEmail}>
        {savingEmail ? 'Sending...' : 'Change Email'}
      </button>

      <div style={{borderTop:'1px solid rgba(255,255,255,0.08)', margin:'1.25rem 0 1rem'}} />

      <label className="settings-section-label">Change Password</label>
      <label className="api-key-label">New Password</label>
      <input type="password" className="api-key-input" value={newPassword}
        onChange={e => setNewPassword(e.target.value)} placeholder="New password" />
      <label className="api-key-label" style={{marginTop:'0.5rem'}}>Confirm Password</label>
      <input type="password" className="api-key-input" value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" />
      {pwStatus && <p className="settings-note" style={{marginTop:'0.4rem', color: pwStatus.includes('updated') ? '#2e7d32' : '#c62828'}}>{pwStatus}</p>}
      <button className="api-key-save-btn" style={{marginTop:'0.75rem'}} onClick={handleChangePassword} disabled={savingPw || !newPassword}>
        {savingPw ? 'Saving...' : 'Change Password'}
      </button>

      <Toast message={toast?.message} type={toast?.type} />
    </div>
  );
}

function NavRail({ onNewChat, onToggleHistory, onOpenProjects, onToggleCouncil, councilDisabled, onOpenCode, onOpenSettings, onOpenHelp, onOpenAccount, onOpenPromptOptimizer, sidebarOpen }) {
  return (
    <div className={`nav-rail${sidebarOpen ? ' nav-rail--mobile-open' : ''}`}>
      <button className="nav-rail-btn" onClick={onNewChat} title="New Chat">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onToggleHistory} title="Chat History">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onOpenProjects} title="Projects">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7v13h18V7"/><path d="M3 7l2-4h14l2 4"/><path d="M3 7h18"/>
        </svg>
      </button>
      <button
        className={`nav-rail-btn${councilDisabled ? ' nav-rail-btn--disabled' : ''}`}
        onClick={onToggleCouncil}
        title={councilDisabled ? 'Configure Council in Settings (need 2+ members + Prime)' : 'Council Mode'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="7" r="3"/><circle cx="17" cy="7" r="3"/><path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6"/><path d="M12 20c0-2.5 2-4.5 5-4.5s5 2 5 4.5"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onOpenCode} title="Code / Developer">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        </svg>
      </button>
      <div className="nav-rail-spacer" />
      <button className="nav-rail-btn" onClick={onOpenPromptOptimizer} title="Prompt Optimizer">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.9L12 2.5z"/>
          <path d="M19 14l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9L19 14z"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onOpenHelp} title="Help">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onOpenSettings} title="Settings">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
        </svg>
      </button>
      <button className="nav-rail-btn nav-rail-btn--account" onClick={onOpenAccount} title="Account">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
        </svg>
      </button>
    </div>
  );
}

function SidebarBottomActions({ onNewChat, onOpenProjects, onToggleCouncil, councilDisabled, onOpenCode, onOpenSettings, onOpenHelp, onOpenAccount, onOpenPromptOptimizer }) {
  return (
    <div className="sidebar-bottom-actions">
      <button className="nav-rail-btn" onClick={onNewChat} title="New Chat">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onOpenProjects} title="Projects">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7v13h18V7"/><path d="M3 7l2-4h14l2 4"/><path d="M3 7h18"/>
        </svg>
      </button>
      <button
        className={`nav-rail-btn${councilDisabled ? ' nav-rail-btn--disabled' : ''}`}
        onClick={onToggleCouncil}
        title={councilDisabled ? 'Configure Council in Settings (need 2+ members + Prime)' : 'Council Mode'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="7" r="3"/><circle cx="17" cy="7" r="3"/><path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6"/><path d="M12 20c0-2.5 2-4.5 5-4.5s5 2 5 4.5"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onOpenCode} title="Code / Developer">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onOpenPromptOptimizer} title="Prompt Optimizer">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.9L12 2.5z"/>
          <path d="M19 14l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9L19 14z"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onOpenHelp} title="Help">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </button>
      <button className="nav-rail-btn" onClick={onOpenSettings} title="Settings">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
        </svg>
      </button>
      <button className="nav-rail-btn nav-rail-btn--account" onClick={onOpenAccount} title="Account">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
        </svg>
      </button>
    </div>
  );
}

const APP_VERSION = '0.1.0';

function PromptOptimizerModal({ initialText, onOptimize, onSend, onClose }) {
  const [draftText, setDraftText]         = React.useState(initialText || '');
  const [optimizedText, setOptimizedText] = React.useState(null);
  const [optimizing, setOptimizing]       = React.useState(false);
  const [error, setError]                 = React.useState(null);

  async function handleOptimize() {
    const text = draftText.trim();
    if (!text) return;
    setOptimizing(true);
    setError(null);
    try {
      const result = await onOptimize(text);
      setOptimizedText(result);
    } catch (e) {
      setError(e.message || 'Could not optimize this prompt.');
    }
    setOptimizing(false);
  }

  function handleSend() {
    if (!optimizedText || !optimizedText.trim()) return;
    onSend(draftText.trim(), optimizedText.trim());
  }

  return (
    <>
      <div style={{position:'fixed',inset:0,zIndex:299}} onClick={onClose} />
      <div className="help-bubble prompt-optimizer-bubble">
        <div className="help-bubble-header">
          <span>Prompt Optimizer</span>
          <button className="help-bubble-close" onClick={onClose}>✕</button>
        </div>
        <div className="help-bubble-body">
          <label className="api-key-label">{optimizedText != null ? 'Optimized prompt' : 'Your prompt'}</label>
          <textarea
            className="api-key-input"
            style={{minHeight:'140px', resize:'vertical', fontFamily:'inherit'}}
            value={optimizedText != null ? optimizedText : draftText}
            onChange={e => (optimizedText != null ? setOptimizedText(e.target.value) : setDraftText(e.target.value))}
            placeholder="Type the prompt you want to send, then click Optimize..."
            autoFocus
          />
          {error && (
            <p className="api-key-test-result api-key-test-result--fail" style={{marginTop:'0.4rem'}}>✕ {error}</p>
          )}
          {optimizedText == null ? (
            <button className="api-key-save-btn" style={{marginTop:'0.75rem'}} onClick={handleOptimize} disabled={optimizing || !draftText.trim()}>
              {optimizing ? 'Optimizing…' : 'Optimize'}
            </button>
          ) : (
            <button className="api-key-save-btn" style={{marginTop:'0.75rem'}} onClick={handleSend} disabled={!optimizedText.trim()}>
              Send
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function HelpBubble({ onClose }) {
  return (
    <>
      <div style={{position:'fixed',inset:0,zIndex:299}} onClick={onClose} />
      <div className="help-bubble">
        <div className="help-bubble-header">
          <span>Help</span>
          <button className="help-bubble-close" onClick={onClose}>✕</button>
        </div>
        <div className="help-bubble-body">
          <div className="help-bubble-section-label">Features</div>
          <ul className="help-bubble-list">
            <li>Chat with Claude, GPT, Gemini, Grok, Mistral, DeepSeek, Perplexity, Groq &amp; more: bring your own API key per model</li>
            <li>Council Mode: dispatch a query to multiple models in parallel with a Prime model synthesizing the results</li>
            <li>Prompt Optimizer: the sparkle icon above rewrites your draft prompt for clarity before you send it</li>
            <li>Projects: track cases, with Key Dates that sync to Google Calendar</li>
            <li>Document attachment: attach PDFs and Word docs for context</li>
            <li>Notes: highlight any part of a response to attach a note to it</li>
            <li>Export: download chats, sources, or prompts as Markdown, Text, PDF, or Word</li>
            <li>Google Drive backup: auto-backs up chats, prompts, sources, and notes to your own Drive (Settings → Backup/Sync), using a sign-in independent of your login</li>
            <li>Rename any chat: hover it in the list and click the pencil icon</li>
            <li>Legal/Open research aperture toggle in the header</li>
          </ul>
          <div className="help-bubble-section-label">Keyboard Shortcuts</div>
          <ul className="help-bubble-list">
            <li><kbd>Enter</kbd>: send message</li>
            <li><kbd>Shift</kbd>+<kbd>Enter</kbd>: insert a newline</li>
          </ul>
          <div className="help-bubble-section-label">Support</div>
          <p className="help-bubble-support">
            See the FAQ tab in Settings for common questions, or use the Feedback tab to reach us directly.<br/>
            <a href="mailto:support@canjur.ai">support@canjur.ai</a>
          </p>
          <div className="help-bubble-version">Canjur v{APP_VERSION}</div>
        </div>
      </div>
    </>
  );
}

function ExportMenu({ onExport, onClose }) {
  return (
    <>
      <div style={{position:'fixed',inset:0,zIndex:249}} onClick={onClose} />
      <div className="export-menu">
        <button onClick={() => { onExport('md'); onClose(); }}>Markdown (.md)</button>
        <button onClick={() => { onExport('txt'); onClose(); }}>Text (.txt)</button>
        <button onClick={() => { onExport('pdf'); onClose(); }}>PDF (.pdf)</button>
        <button onClick={() => { onExport('docx'); onClose(); }}>Word (.docx)</button>
      </div>
    </>
  );
}

function CodePanel({ onClose }) {
  return (
    <div className="code-panel">
      <div className="code-panel-header">
        <span>Code / Developer</span>
        <button onClick={onClose}>✕</button>
      </div>
      <div className="code-panel-body">
        <p>Developer tools are coming soon.</p>
      </div>
    </div>
  );
}

// ── FAQ content ──────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  {
    q: 'Is Canjur a substitute for legal or financial advice?',
    a: 'No. Canjur is a research and drafting assistant, not a licensed professional. Nothing it produces should be relied on without independent review by a qualified attorney, accountant, or other professional -- always verify citations and conclusions before using them in your work.',
  },
  {
    q: 'What is Council Mode?',
    a: 'Council Mode sends your question to multiple AI models at once. One model ("Prime") answers first, the others review that answer and contribute corrections or missing points, and Prime then produces a final, synthesized response. It requires at least two models with API keys configured.',
  },
  {
    q: 'Do I need my own API keys?',
    a: "Yes, for most models -- Canjur uses a bring-your-own-key approach so you control your own usage and costs. Nemotron 3 Super 120B (via OpenRouter) is always available for free without a key. Add keys for other providers under Settings → API Keys & Connections.",
  },
  {
    q: 'How does source/citation verification work?',
    a: 'When available, Canjur checks citations mentioned in a response (statutes, regulations, case citations, etc.) against reference sources and marks which ones it could verify. This is a helpful check, not a guarantee -- always confirm citations independently before relying on them.',
  },
  {
    q: 'Is my Google Drive backup connected to my Canjur login?',
    a: 'No. Drive backup uses its own, separate Google sign-in from Settings → Backup/Sync. The Drive account you back up to does not need to match the Google account (if any) you use to log into Canjur, and you can switch or disconnect it independently at any time.',
  },
  {
    q: 'Where do my backed-up chats go, and who can see them?',
    a: 'Backups are written to a "Canjur" folder created in your own connected Google Drive, under "Canjur/Chat Backups/<chat name>/chat.md" -- alongside that chat\'s prompts, sources, and notes. It\'s your Drive; only accounts you personally share it with can see it, and Canjur does not keep a separate copy elsewhere.',
  },
  {
    q: 'If I rename a chat, does my backup stay in sync?',
    a: "Yes -- the next backup cycle renames the matching Drive folder to match, so your Drive stays organized the same way your chat list does.",
  },
  {
    q: 'How is my PACER login stored?',
    a: 'Your PACER credentials are stored with your account so Canjur can retrieve federal court records on your behalf. Treat them like any other sensitive login, and use the Feedback tab if you ever need them removed.',
  },
  {
    q: 'How do I export a chat, its sources, or its prompts?',
    a: 'Use the export options in the chat, sources, and prompts panels to download as Markdown, plain text, PDF, or DOCX at any time.',
  },
  {
    q: 'What are Notes?',
    a: 'Highlight any part of a response to attach a note to it. Notes are saved per conversation and included automatically in your Google Drive backups.',
  },
  {
    q: 'How do Key Dates and Calendar sync work?',
    a: "Add key dates to a case or project and, if connected, they'll sync to your Google Calendar automatically.",
  },
  {
    q: 'How do I report a bug or request a feature?',
    a: 'Use the Feedback tab in Settings -- it goes straight to the Canjur team.',
  },
];

// ── Settings Panel Component ──────────────────────────────────────────────────
function SettingsPanel({ onClose, initialTab, userKeys, onSaveKey, onDeleteKey, utilityConfig, onSaveUtility, defaultModelId, onSaveDefaultModel, focusModelId, session, councilMembers, councilPrime, onSaveCouncil, allModels, customModels, onCustomModelsChange, theme, onThemeChange, backupEnabled, backupFrequencyMin, backupConnectedEmail, backupConnected, backupStatus, backupLastSyncedAt, onConnectBackupDrive, onDisconnectBackupDrive, onToggleBackupEnabled, onBackupFrequencyChange }) {
  const [activeTab, setActiveTab] = React.useState(focusModelId ? 'keys' : (initialTab || 'account'));
  const [editingModel, setEditingModel] = React.useState(focusModelId || null);
  const [keyDraft, setKeyDraft] = React.useState('');
  const [endpointDraft, setEndpointDraft] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [utilityDraft, setUtilityDraft]           = React.useState(utilityConfig || { modelId: 'groq-llama', apiKey: '', endpoint: '' });
  const [localCouncilMembers, setLocalCouncilMembers] = React.useState(councilMembers || []);
  const [localCouncilPrime, setLocalCouncilPrime]     = React.useState(councilPrime || '');
  const [councilSaved, setCouncilSaved]               = React.useState(false);
  const [showAddCustom, setShowAddCustom]             = React.useState(false);
  const [customLabel, setCustomLabel]                 = React.useState('');
  const [customApiModel, setCustomApiModel]           = React.useState('');
  const [customEndpoint, setCustomEndpoint]           = React.useState('');
  const [customApiKey, setCustomApiKey]               = React.useState('');
  const [customSaving, setCustomSaving]               = React.useState(false);
  const [testingProvider, setTestingProvider]          = React.useState(null);
  const [testResults, setTestResults]                  = React.useState({});
  const [pacerLoginId, setPacerLoginId]                = React.useState('');
  const [pacerPassword, setPacerPassword]              = React.useState('');
  const [pacerSaving, setPacerSaving]                  = React.useState(false);
  const hasPacerKey = !!userKeys['pacer'];
  const MAX_FEEDBACK_CHARS = 1000;
  const MAX_ATTACHMENT_MB  = 3;
  const [feedbackMessage, setFeedbackMessage] = React.useState('');
  const [feedbackFile, setFeedbackFile]       = React.useState(null);
  const [feedbackSending, setFeedbackSending] = React.useState(false);
  const [feedbackResult, setFeedbackResult]   = React.useState(null);
  const feedbackFileInputRef = React.useRef(null);

  async function handleSavePacer() {
    if (!pacerLoginId.trim() || !pacerPassword.trim()) return;
    setPacerSaving(true);
    await onSaveKey('pacer', JSON.stringify({ loginId: pacerLoginId.trim(), password: pacerPassword.trim() }), 'https://pacer.login.uscourts.gov');
    setPacerSaving(false);
    setPacerLoginId('');
    setPacerPassword('');
  }

  async function handleTestConnection(providerId, credentials) {
    setTestingProvider(providerId);
    setTestResults(prev => ({ ...prev, [providerId]: null }));
    try {
      const res = await fetch('/api/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: providerId, ...credentials }),
      });
      const data = await res.json();
      setTestResults(prev => ({ ...prev, [providerId]: { ok: !!data.ok, message: data.message || (data.ok ? 'Connection OK' : 'Connection failed') } }));
    } catch (e) {
      setTestResults(prev => ({ ...prev, [providerId]: { ok: false, message: 'Could not reach test service' } }));
    }
    setTestingProvider(null);
  }

  React.useEffect(() => {
    if (editingModel) {
      const existing = userKeys[editingModel];
      const modelDef = (allModels || MODELS).find(m => m.id === editingModel);
      setKeyDraft(''); // Never pre-fill existing key -- write-only
      setEndpointDraft(existing?.endpoint_url || modelDef?.defaultEndpoint || '');
    }
  }, [editingModel, userKeys, allModels]);

  async function handleSaveKey() {
    if (!editingModel || !keyDraft.trim()) return;
    setSaving(true);
    await onSaveKey(editingModel, keyDraft.trim(), endpointDraft.trim());
    setSaving(false);
    setEditingModel(null);
  }

  async function handleDeleteKey(modelId) {
    await onDeleteKey(modelId);
    if (editingModel === modelId) setEditingModel(null);
  }

  async function handleSaveUtility() {
    setSaving(true);
    await onSaveUtility(utilityDraft);
    setSaving(false);
  }

  function handleFeedbackFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      setFeedbackResult({ ok: false, message: `File too large -- max ${MAX_ATTACHMENT_MB}MB.` });
      e.target.value = '';
      return;
    }
    setFeedbackFile(file);
    setFeedbackResult(null);
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleSubmitFeedback() {
    if (!feedbackMessage.trim() && !feedbackFile) {
      setFeedbackResult({ ok: false, message: 'Please write a message or attach a file.' });
      return;
    }
    setFeedbackSending(true);
    setFeedbackResult(null);
    try {
      let attachment = null;
      if (feedbackFile) {
        const base64 = await fileToBase64(feedbackFile);
        attachment = { filename: feedbackFile.name, contentType: feedbackFile.type || 'application/octet-stream', base64 };
      }
      const res = await fetch('/api/feedback/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: feedbackMessage.trim(), userEmail: session?.user?.email || '', attachment }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFeedbackResult({ ok: true, message: "Thanks -- your feedback was sent." });
        setFeedbackMessage('');
        setFeedbackFile(null);
        if (feedbackFileInputRef.current) feedbackFileInputRef.current.value = '';
      } else {
        setFeedbackResult({ ok: false, message: data.error || 'Could not send feedback. Please try again.' });
      }
    } catch {
      setFeedbackResult({ ok: false, message: 'Could not send feedback. Please try again.' });
    }
    setFeedbackSending(false);
  }

  return (
    <div className="settings-panel">
      <div className="settings-header">
        <span>Settings</span>
        <button className="settings-close" onClick={onClose}>✕</button>
      </div>
      <div className="settings-tabs">
        <button className={`settings-tab ${activeTab === 'account' ? 'settings-tab--active' : ''}`} onClick={() => setActiveTab('account')}>Account &amp; Profile</button>
        <button className={`settings-tab ${activeTab === 'preferences' ? 'settings-tab--active' : ''}`} onClick={() => setActiveTab('preferences')}>Preferences</button>
        <button className={`settings-tab ${activeTab === 'backup' ? 'settings-tab--active' : ''}`} onClick={() => setActiveTab('backup')}>Backup/Sync</button>
        <button className={`settings-tab ${activeTab === 'keys' ? 'settings-tab--active' : ''}`} onClick={() => setActiveTab('keys')}>API Keys &amp; Connections</button>
        <button className={`settings-tab ${activeTab === 'appearance' ? 'settings-tab--active' : ''}`} onClick={() => setActiveTab('appearance')}>Appearance</button>
        <button className={`settings-tab ${activeTab === 'about' ? 'settings-tab--active' : ''}`} onClick={() => setActiveTab('about')}>About Canjur</button>
        <button className={`settings-tab ${activeTab === 'feedback' ? 'settings-tab--active' : ''}`} onClick={() => setActiveTab('feedback')}>Feedback</button>
        <button className={`settings-tab ${activeTab === 'faq' ? 'settings-tab--active' : ''}`} onClick={() => setActiveTab('faq')}>FAQ</button>
      </div>
      <div className="settings-body">

        {activeTab === 'account' && (
          <AccountTab session={session} />
        )}

        {activeTab === 'keys' && (
          <div className="api-keys-section">
            <p className="settings-note" style={{marginBottom:'0.8rem'}}>
              Nemotron 3 Super 120B (OpenRouter) is always available free. Add your own API keys to unlock additional models. Keys are stored securely in your account.
            </p>
            {(allModels || MODELS).filter(m => (m.apiRequired || m.free) && !m.custom).map(model => {
              const hasKey = !!userKeys[model.id];
              const isEditing = editingModel === model.id;
              return (
                <div key={model.id} className={`api-key-row ${isEditing ? 'api-key-row--editing' : ''}`}>
                  <div className="api-key-row-header">
                    <div className="api-key-model-info">
                      <span className="api-key-model-name">{model.label}</span>
                      <span className="api-key-model-sub">{model.sub}</span>
                    </div>
                    <div className="api-key-row-actions">
                      {HOTLINK_URLS[model.sub] && (
                        <button
                          className="api-key-btn api-key-btn--hotlink"
                          onClick={() => window.open(HOTLINK_URLS[model.sub], '_blank', 'noopener,noreferrer')}
                          title={`Get your ${model.sub} API key`}
                        >
                          ↗
                        </button>
                      )}
                      {hasKey ? (
                        <>
                          <span className="api-key-status api-key-status--active">● Active</span>
                          <button className="api-key-btn" onClick={() => setEditingModel(isEditing ? null : model.id)} title="Edit API key">✏️</button>
                          <button className="api-key-btn api-key-btn--delete" onClick={() => handleDeleteKey(model.id)} title="Remove API key">✕</button>
                        </>
                      ) : (
                        <>
                          <span className="api-key-status">○ No key</span>
                          <button className="api-key-btn api-key-btn--add" onClick={() => setEditingModel(isEditing ? null : model.id)} title="Add API key">+ Add</button>
                        </>
                      )}
                    </div>
                  </div>
                  {isEditing && (
                    <div className="api-key-editor">
                      <label className="api-key-label">API Key</label>
                      <input
                        type="password"
                        className="api-key-input"
                        value={keyDraft}
                        onChange={e => setKeyDraft(e.target.value)}
                        placeholder={userKeys[model.id] ? '••••••••••••••••••••••••••••••••• (key set -- enter new key to replace)' : `Enter your ${model.sub} API key`}
                        autoFocus
                      />
                      <label className="api-key-label" style={{marginTop:'0.5rem'}}>Endpoint URL</label>
                      <input
                        type="text"
                        className="api-key-input"
                        value={endpointDraft}
                        onChange={e => setEndpointDraft(e.target.value)}
                        placeholder={model.defaultEndpoint}
                      />
                      <div className="api-key-editor-actions">
                        <button className="api-key-save-btn" onClick={handleSaveKey} disabled={saving || !keyDraft.trim()}>
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="api-key-test-btn"
                          onClick={() => handleTestConnection(model.id, { apiKey: keyDraft.trim(), endpoint: endpointDraft.trim() || model.defaultEndpoint })}
                          disabled={testingProvider === model.id || !keyDraft.trim()}
                        >
                          {testingProvider === model.id ? 'Testing...' : 'Test Connection'}
                        </button>
                        <button className="api-key-cancel-btn" onClick={() => setEditingModel(null)}>Cancel</button>
                      </div>
                      {testResults[model.id] && (
                        <p className={`api-key-test-result ${testResults[model.id].ok ? 'api-key-test-result--ok' : 'api-key-test-result--fail'}`}>
                          {testResults[model.id].ok ? '✓' : '✕'} {testResults[model.id].message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* ── Custom Models ── */}
            <div style={{borderTop:'1px solid #333',marginTop:'1rem',paddingTop:'1rem'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.6rem'}}>
                <span className="settings-section-label" style={{margin:0}}>Custom Models</span>
                <div style={{display:'flex',gap:'0.4rem'}}>
                  <button
                    className="api-key-btn api-key-btn--hotlink"
                    onClick={() => window.open('https://openrouter.ai/keys', '_blank', 'noopener,noreferrer')}
                    title="Get an OpenRouter API key"
                  >
                    ↗ OpenRouter
                  </button>
                  <button
                    className="api-key-btn api-key-btn--add"
                    onClick={() => { setShowAddCustom(v => !v); setCustomLabel(''); setCustomApiModel(''); setCustomEndpoint(''); setCustomApiKey(''); }}
                  >
                    {showAddCustom ? 'Cancel' : '+ Add Model'}
                  </button>
                </div>
              </div>
              <p className="settings-note" style={{marginBottom:'0.6rem'}}>
                Add any OpenAI-compatible API. Works with OpenAI, Groq, Mistral, Together AI, OpenRouter (endpoint: https://openrouter.ai/api/v1), and similar providers.
              </p>
              {(customModels || []).map(cm => {
                const hasKey = !!userKeys[cm.id];
                const isEditing = editingModel === cm.id;
                return (
                  <div key={cm.id} className={`api-key-row ${isEditing ? 'api-key-row--editing' : ''}`}>
                    <div className="api-key-row-header">
                      <div className="api-key-model-info">
                        <span className="api-key-model-name">{cm.label}</span>
                        <span className="api-key-model-sub">{cm.apiModel}</span>
                      </div>
                      <div className="api-key-row-actions">
                        {hasKey
                          ? <span className="api-key-status api-key-status--active">● Active</span>
                          : <span className="api-key-status">○ No key</span>}
                        <button className="api-key-btn" onClick={() => setEditingModel(isEditing ? null : cm.id)} title="Edit API key">✏️</button>
                        <button className="api-key-btn api-key-btn--delete" title="Delete" onClick={async () => {
                          await onDeleteKey(cm.id);
                          onCustomModelsChange((customModels || []).filter(x => x.id !== cm.id));
                        }}>✕</button>
                      </div>
                    </div>
                    {isEditing && (
                      <div className="api-key-editor">
                        <label className="api-key-label">API Key</label>
                        <input type="password" className="api-key-input" value={keyDraft} onChange={e => setKeyDraft(e.target.value)}
                          placeholder={userKeys[cm.id] ? '••••••••••••••••••••••••••••••••• (enter new key to replace)' : `Enter API key for ${cm.label}`} autoFocus />
                        <label className="api-key-label" style={{marginTop:'0.5rem'}}>Endpoint URL</label>
                        <input type="text" className="api-key-input" value={endpointDraft} onChange={e => setEndpointDraft(e.target.value)} placeholder={cm.endpoint} />
                        <div className="api-key-editor-actions">
                          <button className="api-key-save-btn" onClick={handleSaveKey} disabled={saving || !keyDraft.trim()}>{saving ? 'Saving...' : 'Save'}</button>
                          <button className="api-key-cancel-btn" onClick={() => setEditingModel(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {(customModels || []).length === 0 && !showAddCustom && (
                <p className="settings-note" style={{opacity:0.5}}>No custom models added yet.</p>
              )}
              {showAddCustom && (
                <div className="api-key-editor" style={{marginTop:'0.5rem'}}>
                  <label className="api-key-label">Display Name</label>
                  <input type="text" className="api-key-input" value={customLabel} onChange={e => setCustomLabel(e.target.value)} placeholder="e.g. My GPT-4o" />
                  <label className="api-key-label" style={{marginTop:'0.5rem'}}>Model String</label>
                  <input type="text" className="api-key-input" value={customApiModel} onChange={e => setCustomApiModel(e.target.value)} placeholder="e.g. gpt-4o, llama-3.3-70b-versatile" />
                  <label className="api-key-label" style={{marginTop:'0.5rem'}}>Endpoint URL</label>
                  <input type="text" className="api-key-input" value={customEndpoint} onChange={e => setCustomEndpoint(e.target.value)} placeholder="e.g. https://api.openai.com/v1" />
                  <label className="api-key-label" style={{marginTop:'0.5rem'}}>API Key</label>
                  <input type="password" className="api-key-input" value={customApiKey} onChange={e => setCustomApiKey(e.target.value)} placeholder="Your API key" />
                  <div className="api-key-editor-actions">
                    <button
                      className="api-key-save-btn"
                      disabled={customSaving || !customLabel.trim() || !customApiModel.trim() || !customEndpoint.trim() || !customApiKey.trim()}
                      onClick={async () => {
                        setCustomSaving(true);
                        const slug = 'cm_' + Date.now().toString(36);
                        const newModel = { id: slug, label: customLabel.trim(), sub: 'Custom', apiModel: customApiModel.trim(), endpoint: customEndpoint.trim() };
                        await onSaveKey(slug, customApiKey.trim(), customEndpoint.trim());
                        onCustomModelsChange([...(customModels || []), newModel]);
                        setCustomSaving(false);
                        setShowAddCustom(false);
                      }}
                    >
                      {customSaving ? 'Saving...' : 'Add Model'}
                    </button>
                    <button className="api-key-cancel-btn" onClick={() => setShowAddCustom(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>

            {/* ── PACER ── */}
            <div style={{borderTop:'1px solid #333',marginTop:'1rem',paddingTop:'1rem'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.6rem'}}>
                <span className="settings-section-label" style={{margin:0}}>PACER</span>
                <button
                  className="api-key-btn api-key-btn--hotlink"
                  onClick={() => window.open('https://pacer.uscourts.gov/register', '_blank', 'noopener,noreferrer')}
                  title="Register for a PACER account"
                >
                  ↗ Register
                </button>
              </div>
              <p className="settings-note" style={{marginBottom:'0.6rem'}}>
                Federal court records access (US federal courts e-filing/search). {hasPacerKey ? 'Credentials are saved.' : 'Enter your PACER username and password to connect.'}
              </p>
              <label className="api-key-label">PACER Username</label>
              <input
                type="text"
                className="api-key-input"
                value={pacerLoginId}
                onChange={e => setPacerLoginId(e.target.value)}
                placeholder={hasPacerKey ? '(saved, enter to replace)' : 'Your PACER username'}
              />
              <label className="api-key-label" style={{marginTop:'0.5rem'}}>PACER Password</label>
              <input
                type="password"
                className="api-key-input"
                value={pacerPassword}
                onChange={e => setPacerPassword(e.target.value)}
                placeholder={hasPacerKey ? '(saved, enter to replace)' : 'Your PACER password'}
              />
              <div className="api-key-editor-actions" style={{marginTop:'0.5rem'}}>
                <button className="api-key-save-btn" onClick={handleSavePacer} disabled={pacerSaving || !pacerLoginId.trim() || !pacerPassword.trim()}>
                  {pacerSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="api-key-test-btn"
                  onClick={() => handleTestConnection('pacer', { loginId: pacerLoginId.trim(), password: pacerPassword.trim() })}
                  disabled={testingProvider === 'pacer' || !pacerLoginId.trim() || !pacerPassword.trim()}
                >
                  {testingProvider === 'pacer' ? 'Testing...' : 'Test Connection'}
                </button>
              </div>
              {testResults['pacer'] && (
                <p className={`api-key-test-result ${testResults['pacer'].ok ? 'api-key-test-result--ok' : 'api-key-test-result--fail'}`}>
                  {testResults['pacer'].ok ? '✓' : '✕'} {testResults['pacer'].message}
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <>
            <div className="api-keys-section">
              <div className="settings-section-label">Default Model</div>
              <p className="settings-note" style={{marginBottom:'0.8rem'}}>
                The model loaded automatically when you open a chat. Until you add API keys, this stays on the free model it ships with. Add a key, then choose any model you have access to.
              </p>
              <label className="api-key-label">Default Model</label>
              <select className="api-key-input" value={defaultModelId}
                onChange={e => onSaveDefaultModel(e.target.value)}>
                {(allModels || MODELS).map(m => {
                  const hasKey = m.free || !!userKeys[m.id];
                  return <option key={m.id} value={m.id} disabled={!hasKey}>{m.label}{hasKey ? '' : ' (add key first)'}</option>;
                })}
              </select>
            </div>
            <div className="api-keys-section" style={{marginTop:'1rem',borderTop:'1px solid #333',paddingTop:'1rem'}}>
              <div className="settings-section-label">Utility Model</div>
              <p className="settings-note" style={{marginBottom:'0.8rem'}}>
                The utility model handles background tasks: query reformulation, title generation, and citation verification. Choose a fast, cheap model. Groq Llama is fast and cheap with your own Groq key.
              </p>
              <label className="api-key-label">Utility Model</label>
              <select className="api-key-input" value={utilityDraft.modelId}
                onChange={e => {
                  const opt = UTILITY_MODELS.find(u => u.id === e.target.value);
                  setUtilityDraft(prev => ({ ...prev, modelId: e.target.value, endpoint: opt?.endpoint || prev.endpoint }));
                }}>
                {UTILITY_MODELS.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
              </select>
              <label className="api-key-label" style={{marginTop:'0.75rem'}}>API Key (leave blank to use Groq free)</label>
              <input type="password" className="api-key-input" value={utilityDraft.apiKey}
                onChange={e => setUtilityDraft(prev => ({ ...prev, apiKey: e.target.value }))}
                placeholder="API key for selected utility model" />
              <label className="api-key-label" style={{marginTop:'0.5rem'}}>Endpoint URL</label>
              <input type="text" className="api-key-input" value={utilityDraft.endpoint}
                onChange={e => setUtilityDraft(prev => ({ ...prev, endpoint: e.target.value }))}
                placeholder="Auto-filled based on model selection" />
              <button className="api-key-save-btn" style={{marginTop:'0.75rem'}} onClick={handleSaveUtility} disabled={saving}>
                {saving ? 'Saving...' : 'Save Utility Settings'}
              </button>
            </div>
            <div className="api-keys-section" style={{marginTop:'1rem',borderTop:'1px solid #333',paddingTop:'1rem'}}>
              <div className="settings-section-label">Council Mode</div>
              <p className="settings-note" style={{marginBottom:'0.8rem'}}>
                Select which models participate in Council Mode. Requires at least 2 members with API keys. The Prime answers first; others contribute only corrections and new insights; Prime then synthesizes the final response.
              </p>
              <label className="settings-section-label">Members</label>
              {(allModels || MODELS).map(m => {
                const hasKey = m.free || !!userKeys[m.id];
                const checked = localCouncilMembers.includes(m.id);
                return (
                  <label key={m.id} style={{display:'flex',alignItems:'center',gap:'0.5rem',padding:'3px 0',opacity:hasKey?1:0.38,cursor:hasKey?'pointer':'default',fontSize:'0.78rem'}}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!hasKey}
                      onChange={e => {
                        setCouncilSaved(false);
                        if (e.target.checked) {
                          setLocalCouncilMembers(prev => [...prev, m.id]);
                        } else {
                          setLocalCouncilMembers(prev => prev.filter(id => id !== m.id));
                          if (localCouncilPrime === m.id) setLocalCouncilPrime('');
                        }
                      }}
                    />
                    <span>{m.label}</span>
                    <span style={{opacity:0.45,fontSize:'0.68rem'}}>{m.sub}</span>
                    {!hasKey && <span style={{opacity:0.45,fontSize:'0.65rem'}}>(add key first)</span>}
                  </label>
                );
              })}
              <label className="settings-section-label" style={{marginTop:'1rem'}}>Prime Model</label>
              <p className="settings-note" style={{marginBottom:'0.4rem'}}>Answers first and delivers the final synthesis.</p>
              <select
                className="api-key-input"
                value={localCouncilPrime}
                onChange={e => { setLocalCouncilPrime(e.target.value); setCouncilSaved(false); }}
                disabled={localCouncilMembers.length === 0}
              >
                <option value="">-- Select Prime --</option>
                {localCouncilMembers.map(id => {
                  const mo = MODELS.find(m => m.id === id);
                  return mo ? <option key={id} value={id}>{mo.label} ({mo.sub})</option> : null;
                })}
              </select>
              {localCouncilMembers.length < 2 && (
                <p className="settings-note" style={{marginTop:'0.5rem',color:'#c62828',fontSize:'0.72rem'}}>Select at least 2 models to enable Council Mode.</p>
              )}
              <button
                className="api-key-save-btn"
                style={{marginTop:'1rem'}}
                onClick={() => {
                  localStorage.setItem('pm_council_members', JSON.stringify(localCouncilMembers));
                  localStorage.setItem('pm_council_prime', localCouncilPrime);
                  onSaveCouncil(localCouncilMembers, localCouncilPrime);
                  setCouncilSaved(true);
                }}
                disabled={localCouncilMembers.length < 2 || !localCouncilPrime}
              >
                {councilSaved ? 'Saved' : 'Save Council'}
              </button>
            </div>
          </>
        )}

        {activeTab === 'backup' && (
          <div className="api-keys-section">
            <div className="settings-section-label">Backup &amp; Sync</div>
            <p className="settings-note" style={{marginBottom:'0.8rem'}}>
              Automatically back up your chats -- including prompts, sources, and notes -- to Google Drive under "Canjur/Chat Backups/&lt;chat name&gt;". Renaming a chat renames its Drive folder too. Uses its own Google sign-in, separate from your Canjur login account -- the Drive account doesn't have to match.
            </p>
            {backupConnected ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.78rem', color: '#2e7d32', fontWeight: 600, marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1rem', lineHeight: 1 }}>&#10003;</span>
                  <span>Connected to Google Drive</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <button className="api-key-cancel-btn" onClick={onConnectBackupDrive} title="Choose a different Google Drive account">Switch Account</button>
                  <button className="api-key-cancel-btn" onClick={onDisconnectBackupDrive} title="Disconnect backup Drive account">Disconnect</button>
                </div>
                <label style={{display:'flex',alignItems:'center',gap:'0.5rem',padding:'3px 0',cursor:'pointer',fontSize:'0.78rem'}}>
                  <input
                    type="checkbox"
                    checked={backupEnabled}
                    onChange={e => onToggleBackupEnabled(e.target.checked)}
                  />
                  <span>Enable automatic backup</span>
                </label>
                <label className="api-key-label" style={{marginTop:'0.75rem'}}>Sync frequency (every N minutes)</label>
                <input
                  type="number"
                  className="api-key-input"
                  min={1}
                  max={20}
                  value={backupFrequencyMin}
                  disabled={!backupEnabled}
                  onChange={e => onBackupFrequencyChange(parseInt(e.target.value, 10))}
                />
                <p className="settings-note" style={{marginTop:'0.6rem'}}>
                  {!backupEnabled && 'Automatic backup is off -- check the box above to start backing up your chats.'}
                  {backupEnabled && backupStatus === 'syncing' && 'Syncing…'}
                  {backupEnabled && backupStatus === 'paused' && 'Paused (no activity for 10+ minutes)'}
                  {backupEnabled && backupStatus === 'reconnect' && 'Drive connection expired -- please reconnect above.'}
                  {backupEnabled && backupStatus === 'error' && 'Last sync failed -- will retry next cycle.'}
                  {backupEnabled && backupStatus === 'idle' && (backupLastSyncedAt ? `Last synced: ${backupLastSyncedAt.toLocaleTimeString()}` : 'Waiting for the first sync…')}
                </p>
              </>
            ) : (
              <button className="gdrive-connect-btn" onClick={onConnectBackupDrive}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.21 1.18 2 2 0 012.18 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
                Connect Google Drive for Backup
              </button>
            )}
          </div>
        )}

        {activeTab === 'appearance' && (
          <div className="api-keys-section">
            <div className="settings-section-label">Theme</div>
            <p className="settings-note" style={{marginBottom:'0.6rem'}}>Choose how Canjur looks.</p>
            <div className="theme-toggle-row">
              <button
                className={`theme-toggle-btn ${theme === 'light' ? 'theme-toggle-btn--active' : ''}`}
                onClick={() => onThemeChange('light')}
              >
                ☀ Light
              </button>
              <button
                className={`theme-toggle-btn ${theme === 'dark' ? 'theme-toggle-btn--active' : ''}`}
                onClick={() => onThemeChange('dark')}
              >
                ☾ Dark
              </button>
            </div>
          </div>
        )}

        {activeTab === 'about' && (
          <div className="api-keys-section">
            <div className="settings-section-label">Canjur</div>
            <p className="settings-note">Version {APP_VERSION}</p>
            <p className="settings-note" style={{marginTop:'0.5rem'}}>
              CANJUR is a completely fresh approach to legal/financial and even general use ai chat assistance. Our mission is to provide a tool which produces superior results. That means accuracy beyond a reasonable doubt. Canjur is not simply an ai research assistant, it is a council. Work confidently and free of the inhibitions and errors of products which rely on "training". They are stuck on the models they trained on. Canjur is ai unchained.
            </p>
            <p className="settings-note" style={{marginTop:'0.5rem'}}>
              Support: <a href="mailto:support@canjur.ai">support@canjur.ai</a>
            </p>
          </div>
        )}

        {activeTab === 'feedback' && (
          <div className="api-keys-section">
            <div className="settings-section-label">Feedback &amp; Requests</div>
            <p className="settings-note" style={{marginBottom:'0.8rem'}}>
              Found a bug, have a feature idea, or just want to share feedback? Send it directly to the Canjur team.
            </p>
            <label className="api-key-label">Your message</label>
            <textarea
              className="api-key-input"
              style={{minHeight:'120px', resize:'vertical', fontFamily:'inherit'}}
              value={feedbackMessage}
              maxLength={MAX_FEEDBACK_CHARS}
              onChange={e => setFeedbackMessage(e.target.value)}
              placeholder="Tell us what's working, what's not, or what you'd like to see..."
            />
            <p className="settings-note" style={{marginTop:'0.3rem', textAlign:'right', fontSize:'0.65rem'}}>
              {feedbackMessage.length} / {MAX_FEEDBACK_CHARS}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop:'0.5rem', flexWrap:'wrap' }}>
              <button className="api-key-cancel-btn" onClick={() => feedbackFileInputRef.current.click()}>
                {feedbackFile ? 'Change File' : 'Attach File'}
              </button>
              {feedbackFile && (
                <>
                  <span style={{fontSize:'0.72rem', color:'var(--text-dim)'}}>{feedbackFile.name}</span>
                  <button
                    className="api-key-btn api-key-btn--delete"
                    onClick={() => { setFeedbackFile(null); if (feedbackFileInputRef.current) feedbackFileInputRef.current.value = ''; }}
                    title="Remove attachment"
                  >✕</button>
                </>
              )}
              <input ref={feedbackFileInputRef} type="file" style={{ display: 'none' }} onChange={handleFeedbackFileChange} />
            </div>
            <p className="settings-note" style={{marginTop:'0.3rem', fontSize:'0.65rem'}}>
              Optional -- for more in-depth feedback or feature requests. Max {MAX_ATTACHMENT_MB}MB.
            </p>
            <button
              className="api-key-save-btn"
              style={{marginTop:'1rem'}}
              onClick={handleSubmitFeedback}
              disabled={feedbackSending || (!feedbackMessage.trim() && !feedbackFile)}
            >
              {feedbackSending ? 'Sending...' : 'Submit'}
            </button>
            {feedbackResult && (
              <p className={`api-key-test-result ${feedbackResult.ok ? 'api-key-test-result--ok' : 'api-key-test-result--fail'}`} style={{marginTop:'0.5rem'}}>
                {feedbackResult.ok ? '✓' : '✕'} {feedbackResult.message}
              </p>
            )}
          </div>
        )}

        {activeTab === 'faq' && (
          <div className="api-keys-section">
            <div className="settings-section-label">Frequently Asked Questions</div>
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} style={{marginTop: i === 0 ? '0.2rem' : '0.9rem'}}>
                <p className="settings-note" style={{fontWeight: 600, color: 'var(--text)'}}>{item.q}</p>
                <p className="settings-note" style={{marginTop: '0.25rem'}}>{item.a}</p>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

export default function Chat({ session, onLogout }) {
  const [conversations, setConversations]     = useState([]);
  const [currentConvId, setCurrentConvId]     = useState(null);
  // Synchronous mirror of currentConvId, plus the in-flight create. State
  // updates are not visible to code already running in the same render, and a
  // single send resolves the conversation more than once. See
  // ensureConversation.
  const convIdRef     = useRef(null);
  const convCreateRef = useRef(null);
  const [renamingConvId, setRenamingConvId]   = useState(null);
  const [renameValue, setRenameValue]         = useState('');
  const [messages, setMessages]               = useState([]);
  const [history, setHistory]                 = useState([]);
  const [input, setInput]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const [sidebarOpen, setSidebarOpen]         = useState(false);
  const [attachedDocs, setAttachedDocs]       = useState([]);
  const [dragOver, setDragOver]               = useState(false);
  const [defaultModelId, setDefaultModelId]   = useState(() => localStorage.getItem('pm_default_model') || MODELS[0].id);
  const [selectedModel, setSelectedModel]     = useState(() => {
    const saved = localStorage.getItem('pm_default_model');
    return (saved && MODELS.find(m => m.id === saved)) || MODELS[0];
  });
  const [promptHistory, setPromptHistory]     = useState([]);
  const [verifiedCitations, setVerifiedCitations] = useState({});
  const [caseTrackerOpen, setCaseTrackerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen]       = useState(false);
  const [convPct, setConvPct]                 = useState(50);
  const [convCollapsed, setConvCollapsed]     = useState(false);
  const [cases, setCases]                     = useState([]);
  const [newCaseName, setNewCaseName]         = useState('');
  const [casesLoading, setCasesLoading]        = useState(false);
  const [projectEvents, setProjectEvents]      = useState({}); // { [projectId]: [{id, title, event_date}] }
  const [expandedCaseId, setExpandedCaseId]    = useState(null);
  const [newEventTitle, setNewEventTitle]      = useState('');
  const [newEventDate, setNewEventDate]        = useState('');
  const [bottomTab, setBottomTab]             = useState('prompts'); // 'prompts' | 'manifest'
  const [sourceManifest, setSourceManifest]   = useState(null); // { corpus: [], live: [] }
  const [aperture, setAperture]               = useState('legal'); // 'legal' | 'open'
  const [userKeys, setUserKeys]               = useState({}); // {modelId: {api_key, endpoint_url}}
  // Apply the chosen default model once keys are known; until the account has a
  // key for it, fall back to the free model it ships with.
  useEffect(() => {
    const m = MODELS.find(x => x.id === defaultModelId);
    if (m) setSelectedModel(m.free || userKeys[m.id] ? m : MODELS[0]);
  }, [userKeys, defaultModelId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [utilityConfig, setUtilityConfig]     = useState({ modelId: 'groq-llama', apiKey: '', endpoint: 'https://api.groq.com/openai/v1' });
  const [focusSettingsModel, setFocusSettingsModel] = useState(null);
  const [focusSettingsTab, setFocusSettingsTab]     = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed]     = useState(false);
  const [councilMode, setCouncilMode]               = useState(false);
  const [projectQActive, setProjectQActive]         = useState(false);
  const [privateModeActive, setPrivateModeActive]   = useState(false);
  const [councilMembers, setCouncilMembers]         = useState(() => { try { return JSON.parse(localStorage.getItem('pm_council_members') || '[]'); } catch { return []; } });
  const [councilPrime, setCouncilPrime]             = useState(() => localStorage.getItem('pm_council_prime') || '');
  const [customModels, setCustomModels]             = useState(() => {
    try { return JSON.parse(localStorage.getItem(`pm_custom_models_${session.user.id}`) || '[]'); } catch { return []; }
  });
  const [sessionContext, setSessionContext]         = useState(null);
  const [notes, setNotes]                     = useState([]);
  const [contextMenu, setContextMenu]         = useState(null);
  const [activeNote, setActiveNote]           = useState(null);
  const [noteHighlight, setNoteHighlight]     = useState(null);
  const [helpOpen, setHelpOpen]               = useState(false);
  const [promptOptimizerOpen, setPromptOptimizerOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen]   = useState(null); // 'chat' | 'sources' | 'prompts' | null
  const [codePanelOpen, setCodePanelOpen]     = useState(false);
  const [theme, setTheme]                     = useState(() => localStorage.getItem('pm_theme') || 'light');
  const [backupEnabled, setBackupEnabled]     = useState(() => localStorage.getItem('pm_backup_enabled') === 'true');
  const [backupFrequencyMin, setBackupFrequencyMin] = useState(
    () => Math.min(20, Math.max(1, Number(localStorage.getItem('pm_backup_frequency_min')) || 5))
  );
  const [backupConnectedEmail, setBackupConnectedEmail] = useState(() => getBackupAccountEmail());
  const [backupConnected, setBackupConnected] = useState(() => isBackupConnected());
  const [backupStatus, setBackupStatus]       = useState('idle'); // 'idle' | 'syncing' | 'paused' | 'error' | 'reconnect'
  const [backupLastSyncedAt, setBackupLastSyncedAt] = useState(null);

  const sidebarRef      = useRef(null);
  const draggingRef     = useRef(false);
  const messagesEndRef  = useRef(null);
  const atBottomRef     = useRef(true);
  const textareaRef     = useRef(null);
  const fileInputRef    = useRef(null);
  const abortCtrlRef    = useRef(null); // for stream interruption
  const lastActivityRef = useRef(Date.now());
  const isSyncingRef    = useRef(false);

  // ── Draggable divider ──────────────────────────────────────────────────────
  function handleDividerMouseDown(e) {
    e.preventDefault();
    draggingRef.current = true;
    document.addEventListener('mousemove', handleDividerMouseMove);
    document.addEventListener('mouseup', handleDividerMouseUp);
  }

  function handleDividerMouseMove(e) {
    if (!draggingRef.current || !sidebarRef.current) return;
    const rect = sidebarRef.current.getBoundingClientRect();
    const pct  = Math.min(85, Math.max(15, ((e.clientY - rect.top) / rect.height) * 100));
    setConvPct(pct);
  }

  function handleDividerMouseUp() {
    draggingRef.current = false;
    document.removeEventListener('mousemove', handleDividerMouseMove);
    document.removeEventListener('mouseup', handleDividerMouseUp);
  }

  useEffect(() => { loadConversations(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadUserKeys(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadProjects(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { localStorage.setItem('pm_theme', theme); }, [theme]);
  useEffect(() => { if (atBottomRef.current) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // ── Drive backup: pick up OAuth redirect fragment/error (independent of login) ──
  // The redirect back from Google is a full page reload (not a client-side
  // route change), so settingsOpen/activeTab reset to their defaults. This
  // effect re-opens Settings on the Backup/Sync tab so the result (success
  // or failure) is actually visible instead of landing on a closed panel.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('gdrive_backup_token')) {
      const params = new URLSearchParams(hash.slice(1));
      const token   = params.get('gdrive_backup_token');
      const refresh = params.get('gdrive_backup_refresh');
      const expiry  = params.get('gdrive_backup_expiry');
      const email   = params.get('gdrive_backup_email') || '';
      if (token && refresh) {
        localStorage.setItem('pm_backup_gdrive_token', token);
        localStorage.setItem('pm_backup_gdrive_refresh', refresh);
        localStorage.setItem('pm_backup_gdrive_expiry', expiry || String(Date.now() + 3600000));
        localStorage.setItem('pm_backup_gdrive_email', email);
        setBackupConnectedEmail(email);
        setBackupConnected(true);
      }
      window.history.replaceState({}, '', window.location.pathname + window.location.search);
      setSettingsOpen(true);
      setFocusSettingsTab('backup');
      return;
    }
    const backupError = new URLSearchParams(window.location.search).get('gdrive_backup_error');
    if (backupError) {
      window.history.replaceState({}, '', window.location.pathname);
      alert(`Google Drive backup connection failed: ${backupError.replace(/_/g, ' ')}. Please try connecting again.`);
      setSettingsOpen(true);
      setFocusSettingsTab('backup');
    }
  }, []);

  // ── Drive backup: track user activity for the 10-minute idle auto-pause ───
  useEffect(() => {
    const markActive = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('mousemove', markActive);
    window.addEventListener('keydown', markActive);
    window.addEventListener('click', markActive);
    return () => {
      window.removeEventListener('mousemove', markActive);
      window.removeEventListener('keydown', markActive);
      window.removeEventListener('click', markActive);
    };
  }, []);

  // ── Drive backup: one sync cycle across all conversations ─────────────────
  const IDLE_PAUSE_MS = 10 * 60 * 1000;

  async function runBackupCycle() {
    if (isSyncingRef.current) return;
    if (Date.now() - lastActivityRef.current > IDLE_PAUSE_MS) {
      setBackupStatus('paused');
      return;
    }
    isSyncingRef.current = true;
    setBackupStatus('syncing');
    try {
      const token = await ensureBackupToken();
      if (!token) {
        setBackupStatus('reconnect');
        return;
      }

      const rootFolderId = await ensureBackupRootFolder(token);

      const convRows = await sbGet('conversations', {
        select: 'id,title,created_at,messages!inner(id)',
        hidden: 'eq.false',
        order: 'created_at.desc',
        limit: 40
      }, session.token);
      const convs = Array.isArray(convRows) ? convRows : [];
      const backupMap = getBackupMap();

      for (const conv of convs) {
        const msgRows = await sbGet('messages', {
          select: 'id,role,content,verified_citations',
          conversation_id: `eq.${conv.id}`,
          order: 'created_at.asc'
        }, session.token);
        const allRows   = Array.isArray(msgRows) ? msgRows : [];
        const chatRows  = allRows.filter(m => m.role !== 'prompt_meta');
        const promptTexts = allRows.filter(m => m.role === 'prompt_meta').map(m => m.content);

        const lastWithManifest = [...chatRows].reverse().find(m => m.role === 'assistant' && m.content);
        const sourceManifest = lastWithManifest ? parseSourceManifest(lastWithManifest.content) : null;

        let noteRows = [];
        try {
          const nRows = await sbGet('notes', { conversation_id: `eq.${conv.id}`, order: 'created_at.asc' }, session.token);
          noteRows = Array.isArray(nRows) ? nRows : [];
        } catch { /* notes are best-effort; a fetch failure shouldn't block the chat backup */ }

        const markdownText = buildBackupMarkdown(chatRows, sourceManifest, promptTexts, noteRows);
        const newHash = hashText(markdownText);
        const cached = backupMap[conv.id];
        // Skip only if BOTH content and title are unchanged -- a pure rename
        // (identical content) must still go through to trigger the folder rename.
        if (cached && cached.contentHash === newHash && cached.title === conv.title) continue;

        await syncConversationToDrive(token, rootFolderId, conv, markdownText);
        await new Promise(r => setTimeout(r, 300)); // stay well under Drive rate limits
      }

      setBackupLastSyncedAt(new Date());
      setBackupStatus('idle');
    } catch (e) {
      console.error('[runBackupCycle]', e);
      setBackupStatus('error');
    } finally {
      isSyncingRef.current = false;
    }
  }

  useEffect(() => {
    if (!backupEnabled || !backupConnected) return;
    const intervalId = setInterval(runBackupCycle, backupFrequencyMin * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [backupEnabled, backupFrequencyMin, backupConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Citation verification ──────────────────────────────────────────────────
  const verifyCitations = useCallback(async (msgIndex, text, convId, messageDbId) => {
    const citations = extractCitations(text);
    if (!citations.length) return;
    const verified = new Set();
    await Promise.all(citations.map(async (citation) => {
      try {
        if (/U\.S\.C\.|C\.F\.R\.|UCC\s+§|Am\.?\s*Jur|C\.J\.S\./.test(citation)) {
          verified.add(citation);
          return;
        }
        const q = citation.replace(/[^a-zA-Z0-9\s]/g, ' ').trim().slice(0, 60);
        const resp = await fetch(
          `https://www.courtlistener.com/api/rest/v4/search/?q=${encodeURIComponent(q)}&type=o&stat_Published=on`,
          { headers: { 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(8000) }
        );
        if (resp.ok) {
          const data = await resp.json();
          if (data.results && data.results.length > 0) verified.add(citation);
        }
      } catch {}
    }));
    setVerifiedCitations(prev => ({ ...prev, [msgIndex]: verified }));

    // Save to database if we have a message ID
    if (messageDbId && verified.size > 0) {
      try {
        const patchRes = await fetch(`${SB_URL}/rest/v1/messages?id=eq.${messageDbId}`, {
          method: 'PATCH',
          headers: {
            'apikey':        SB_KEY,
            'Authorization': `Bearer ${session.token}`,
            'Content-Type':  'application/json',
            'Prefer':        'return=minimal',
          },
          body: JSON.stringify({ verified_citations: Array.from(verified) }),
        });
        if (!patchRes.ok) {
          const errText = await patchRes.text();
          console.error('[Citations] PATCH failed:', patchRes.status, errText);
        } else {
          console.log('[Citations] Saved', verified.size, 'citations for message', messageDbId);
        }
      } catch (e) {
        console.error('[Citations] PATCH error:', e);
      }
    } else {
      console.log('[Citations] Skipping save — messageDbId:', messageDbId, 'verified.size:', verified.size);
    }
  }, [session.token]);

  // ── Conversations ──────────────────────────────────────────────────────────
  async function loadConversations() {
    const data = await sbGet('conversations', {
      select: 'id,title,created_at,messages!inner(id)',
      hidden: 'eq.false',
      order: 'created_at.desc',
      limit: 40
    }, session.token);
    const convs = Array.isArray(data) ? data : [];
    setConversations(convs);
    if (convs.length > 0) loadConversation(convs[0].id);
    else startNewChat();
  }

  async function refreshConversations() {
    const data = await sbGet('conversations', {
      select: 'id,title,created_at,messages!inner(id)',
      hidden: 'eq.false',
      order: 'created_at.desc',
      limit: 40
    }, session.token);
    const convs = Array.isArray(data) ? data : [];
    setConversations(convs);
  }

  async function hideConversation(convId) {
    try {
      await fetch(`${SB_URL}/rest/v1/conversations?id=eq.${convId}`, {
        method: 'PATCH',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}`, 'Content-Type': 'application/json', 'Prefer': 'return=minimal' },
        body: JSON.stringify({ hidden: true }),
      });
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (currentConvId === convId) startNewChat();
    } catch {}
  }

  async function loadConversation(convId) {
    convIdRef.current = convId;
    convCreateRef.current = null;
    setCurrentConvId(convId);
    setHistory([]);
    setSessionContext(null);
    setPromptHistory([]);
    setVerifiedCitations({});
    setSourceManifest(null);
    const data = await sbGet('messages', {
      select: 'id,role,content,verified_citations',
      conversation_id: `eq.${convId}`,
      order: 'created_at.asc'
    }, session.token);
    if (!Array.isArray(data)) {
      console.error('[loadConversation] unexpected response:', data);
    }
    const rows = Array.isArray(data) ? data : [];
    const chatRows   = rows.filter(m => m.role !== 'prompt_meta');
    const promptRows = rows.filter(m => m.role === 'prompt_meta');

    // Restore verified citations from database
    const restoredVerified = {};
    chatRows.forEach((m, idx) => {
      if (m.verified_citations && m.verified_citations.length > 0) {
        restoredVerified[idx] = new Set(m.verified_citations);
      }
    });
    if (Object.keys(restoredVerified).length > 0) {
      setVerifiedCitations(restoredVerified);
    }

    function parseRow(m) {
      if (m.role === 'assistant' && m.content && m.content.startsWith('[PM_MODEL:')) {
        const end  = m.content.indexOf(']');
        const meta = m.content.slice(10, end).split('|');
        return { role: m.role, content: m.content.slice(end + 1),
                 modelLabel: meta[0] || '', modelSub: meta[1] || '', dbId: m.id };
      }
      return { role: m.role, content: m.content, dbId: m.id };
    }
    setHistory(chatRows.map(m => {
      if (m.role === 'assistant' && m.content && m.content.startsWith('[PM_MODEL:')) {
        const end = m.content.indexOf(']');
        return { role: m.role, content: m.content.slice(end + 1) };
      }
      return { role: m.role, content: m.content };
    }));
    const parsedRows = chatRows.map(parseRow);
    // Restore source manifest from the last assistant message that has one
    const lastWithManifest = [...parsedRows].reverse().find(m => m.role === 'assistant' && m.content);
    if (lastWithManifest) {
      const restoredManifest = parseSourceManifest(lastWithManifest.content);
      if (restoredManifest) {
        setSourceManifest(restoredManifest);
        setBottomTab('manifest');
      }
    }
    setMessages(parsedRows);
    setPromptHistory(promptRows.map(m => m.content));
    try {
      const noteRows = await sbGet('notes', { conversation_id: `eq.${convId}`, order: 'created_at.asc' }, session.token);
      if (Array.isArray(noteRows)) setNotes(noteRows);
    } catch {}
  }

  async function loadUserKeys() {
    try {
      const rows = await sbGet(
        'user_api_keys',
        { user_id: `eq.${session.user.id}`, select: 'model_id,endpoint_url' },
        session.token
      );
      if (Array.isArray(rows)) {
        const map = {};
        rows.forEach(row => { map[row.model_id] = { api_key: '••••••••', endpoint_url: row.endpoint_url }; }); // api_key masked client-side
        setUserKeys(map);
        try {
          const urows = await sbGet(
            'user_api_keys',
            { user_id: `eq.${session.user.id}`, model_id: 'eq.__utility__', select: 'api_key' },
            session.token
          );
          if (Array.isArray(urows) && urows[0]?.api_key) {
            try { setUtilityConfig(JSON.parse(urows[0].api_key)); } catch {}
          }
        } catch {}
      }
    } catch (e) { console.error('loadUserKeys:', e); }
  }

  async function saveUserKey(modelId, apiKey, endpointUrl) {
    try {
      await fetch(`${SB_URL}/rest/v1/rpc/save_encrypted_api_key`, {
        method: 'POST',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}`,
          'Content-Type': 'application/json' },
        body: JSON.stringify({ p_user_id: session.user.id, p_model_id: modelId, p_api_key: apiKey, p_endpoint_url: endpointUrl })
      });
      setUserKeys(prev => ({ ...prev, [modelId]: { api_key: apiKey, endpoint_url: endpointUrl } }));
    } catch (e) { console.error('saveUserKey:', e); }
  }

  async function deleteUserKey(modelId) {
    try {
      await fetch(`${SB_URL}/rest/v1/user_api_keys?user_id=eq.${session.user.id}&model_id=eq.${modelId}`, {
        method: 'DELETE',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}` }
      });
      setUserKeys(prev => { const n = {...prev}; delete n[modelId]; return n; });
    } catch (e) { console.error('deleteUserKey:', e); }
  }

  async function saveUtilityConfig(config) {
    setUtilityConfig(config);
    await saveUserKey('__utility__', JSON.stringify(config), '');
  }

  function saveCustomModels(newList) {
    localStorage.setItem(`pm_custom_models_${session.user.id}`, JSON.stringify(newList));
    setCustomModels(newList);
  }

  const allModels = useMemo(() => [
    ...MODELS,
    ...customModels.map(cm => ({
      id: cm.id,
      label: cm.label,
      sub: cm.sub || 'Custom',
      url: GENERIC_BRIDGE_URL,
      model: cm.apiModel,
      apiRequired: true,
      defaultEndpoint: cm.endpoint,
      custom: true,
    })),
  ], [customModels]);

  function startNewChat() {
    convIdRef.current = null;
    convCreateRef.current = null;
    setCurrentConvId(null);
    setHistory([]);
    setMessages([]);
    setSessionContext(null);
    setAttachedDocs([]);
    setPromptHistory([]);
    setVerifiedCitations({});
    setSourceManifest(null);
    setNotes([]);
    setContextMenu(null);
    setActiveNote(null);
    setSidebarOpen(false);
    setProjectQActive(false);
    setPrivateModeActive(false);
  }

  // Why this guards on a ref and not on currentConvId:
  //
  // A single send calls ensureConversation twice, once to log prompt_meta and
  // again to save the user message. currentConvId is state, so setCurrentConvId
  // in the first call does NOT update the value closed over by the second call
  // in that same render. The guard therefore missed, and every new chat created
  // TWO conversation rows with the same title: the first holding only
  // prompt_meta rows, which the transcript does not render, so it showed up in
  // the sidebar as a blank duplicate sitting just above the real chat.
  //
  // convIdRef updates synchronously, so the second call sees the first one's
  // row. createRef additionally collapses concurrent callers onto one in-flight
  // insert, so a double-fire cannot race past the id check either.
  async function ensureConversation(firstMessage) {
    if (convIdRef.current) return convIdRef.current;
    if (currentConvId) { convIdRef.current = currentConvId; return currentConvId; }
    if (convCreateRef.current) return convCreateRef.current;

    convCreateRef.current = (async () => {
      const title = firstMessage.slice(0, 48) + (firstMessage.length > 48 ? '...' : '');
      const data  = await sbPost('conversations', { user_id: session.user.id, title }, session.token);
      const row   = Array.isArray(data) ? data[0] : data;
      if (!row || !row.id) return null;
      convIdRef.current = row.id;
      setCurrentConvId(row.id);
      setConversations(prev => [{ id: row.id, title, created_at: new Date().toISOString() }, ...prev]);
      return row.id;
    })();

    try { return await convCreateRef.current; }
    finally { convCreateRef.current = null; }
  }

  async function renameConversation(convId, newTitle) {
    const title = newTitle.trim();
    setRenamingConvId(null);
    if (!title) return;
    try {
      await fetch(`${SB_URL}/rest/v1/conversations?id=eq.${convId}`, {
        method: 'PATCH',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}`, 'Content-Type': 'application/json', 'Prefer': 'return=minimal' },
        body: JSON.stringify({ title }),
      });
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, title } : c));
    } catch {}
  }

  async function generateConversationTitle(convId, userText, assistantText) {
    if (!utilityConfig.apiKey) return;
    const model = UTILITY_MODELS.find(u => u.id === utilityConfig.modelId);
    if (!model) return;
    try {
      const resp = await fetch(`${utilityConfig.endpoint}/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${utilityConfig.apiKey}` },
        body: JSON.stringify({
          model: model.model,
          messages: [
            { role: 'system', content: 'Generate a concise 4–6 word title for this conversation. Reply with ONLY the title, no quotes, no trailing punctuation.' },
            { role: 'user', content: `User asked: ${userText.slice(0, 200)}\nAssistant responded: ${assistantText.slice(0, 400)}` },
          ],
          max_tokens: 20,
          stream: false,
        }),
        signal: AbortSignal.timeout(8000),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const title = data.choices?.[0]?.message?.content?.trim();
      if (!title) return;
      await fetch(`${SB_URL}/rest/v1/conversations?id=eq.${convId}`, {
        method: 'PATCH',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}`, 'Content-Type': 'application/json', 'Prefer': 'return=minimal' },
        body: JSON.stringify({ title }),
      });
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, title } : c));
    } catch {}
  }

  async function generateSessionContext(firstUserText, firstAssistantText) {
    if (!utilityConfig.apiKey || sessionContext) return;
    const model = UTILITY_MODELS.find(u => u.id === utilityConfig.modelId);
    if (!model) return;
    try {
      const resp = await fetch(`${utilityConfig.endpoint}/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${utilityConfig.apiKey}` },
        body: JSON.stringify({
          model: model.model,
          messages: [
            { role: 'system', content: 'Extract a structured session context from the opening exchange of a legal research conversation. Include: matter type, jurisdiction(s), key parties or statutes involved, and the primary legal question being researched. Be concise, 80 to 120 words. Plain prose, no markdown, no bullet points.' },
            { role: 'user', content: `User: ${firstUserText.slice(0, 400)}\n\nAssistant: ${firstAssistantText.slice(0, 800)}` },
          ],
          max_tokens: 200,
          stream: false,
        }),
        signal: AbortSignal.timeout(10000),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const ctx = data.choices?.[0]?.message?.content?.trim();
      if (ctx) setSessionContext(ctx);
    } catch {}
  }

  async function optimizePromptText(rawText) {
    if (!utilityConfig.apiKey) {
      throw new Error('Add a Utility Model API key in Settings → Preferences to use the optimizer.');
    }
    const model = UTILITY_MODELS.find(u => u.id === utilityConfig.modelId);
    if (!model) throw new Error('No utility model configured.');
    const resp = await fetch(`${utilityConfig.endpoint}/chat/completions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${utilityConfig.apiKey}` },
      body: JSON.stringify({
        model: model.model,
        messages: [
          { role: 'system', content: "You improve draft prompts for an AI legal/financial/general research assistant. Rewrite the user's prompt to be clearer, more specific, and more likely to produce a thorough, well-sourced answer. Preserve the user's original intent, facts, names, and dates exactly -- never invent new facts. Reply with ONLY the rewritten prompt: no preamble, no quotes, no explanation." },
          { role: 'user', content: rawText },
        ],
        max_tokens: 600,
        stream: false,
      }),
      signal: AbortSignal.timeout(20000),
    });
    if (!resp.ok) throw new Error('Optimizer request failed. Please try again.');
    const data = await resp.json();
    const optimized = data.choices?.[0]?.message?.content?.trim();
    if (!optimized) throw new Error('Optimizer returned an empty result.');
    return optimized;
  }

  function handlePromptOptimizerSend(originalText, optimizedText) {
    setPromptOptimizerOpen(false);
    sendQuery(optimizedText, originalText);
  }

  async function saveMessage(convId, role, content) {
    if (!convId) return null;
    const data = await sbPost('messages', { conversation_id: convId, role, content }, session.token);
    const row = Array.isArray(data) ? data[0] : data;
    return row?.id || null;
  }

  // ── File handling ──────────────────────────────────────────────────────────
  async function extractText(file) {
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'txt' || ext === 'md') return await file.text();
    if (ext === 'pdf') {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let text = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page    = await pdf.getPage(i);
        const content = await page.getTextContent();
        text += content.items.map(item => item.str).join(' ') + '\n';
      }
      return text;
    }
    if (ext === 'docx') {
      const arrayBuffer = await file.arrayBuffer();
      const result      = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    }
    return null;
  }

  async function handleFiles(fileList) {
    const files = Array.from(fileList || []);
    const added = [];
    let unsupported = false;
    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['txt', 'md', 'pdf', 'docx'].includes(ext)) { unsupported = true; continue; }
      const text = await extractText(file);
      if (text) added.push({ name: file.name, text });
    }
    if (unsupported) alert('Supported formats: PDF, DOCX, TXT, MD');
    if (added.length) {
      setAttachedDocs(prev => [...prev.filter(d => !added.some(a => a.name === d.name)), ...added]);
    }
  }

  function buildDocPrefix(docs) {
    return docs.map(d => `[Document: ${d.name}]\n\n${d.text}`).join('\n\n---\n\n') + '\n\n---\n\n';
  }

  function handleFileInput(e) { handleFiles(e.target.files); e.target.value = ''; }
  function handleDragOver(e) { e.preventDefault(); setDragOver(true); }
  function handleDragLeave() { setDragOver(false); }
  function handleDrop(e) {
    e.preventDefault(); setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }

  // ── Interrupt / stop stream ────────────────────────────────────────────────
  function handleStopStream() {
    if (abortCtrlRef.current) {
      abortCtrlRef.current.abort();
      abortCtrlRef.current = null;
    }
  }

  // ── Council helpers ────────────────────────────────────────────────────────
  // Returns { text, notices }. Never throws except on a user abort.
  async function callModelFull(model, messages, abortSignal) {
    return fetchBridge(model.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${model.apiKey || API_KEY}` },
      body: JSON.stringify({
        model: model.model, messages, stream: true, aperture,
        model_id: model.id,
        user_id: session?.user?.id || '',
        utility_model: UTILITY_MODELS.find(u => u.id === utilityConfig.modelId)?.model || 'llama-3.3-70b-versatile',
        utility_api_key: utilityConfig.apiKey || '',
        utility_url: utilityConfig.endpoint || 'https://api.groq.com/openai/v1',
      }),
    }, abortSignal, null, model.label);
  }

  function setCouncilStatus(content) {
    setMessages(prev => {
      const updated = [...prev];
      updated[updated.length - 1] = { role: 'assistant', content, modelLabel: 'The Council', modelSub: '' };
      return updated;
    });
  }

  async function sendCouncilQuery(text, originalForLog, docsOverride) {
    setInput('');
    setLoading(true);
    abortCtrlRef.current = new AbortController();
    const sig = abortCtrlRef.current.signal;
    const docs = docsOverride || attachedDocs;
    try {

    setPromptHistory(prev => [...prev, text]);
    const convIdForPrompt = currentConvId || await ensureConversation(text);
    if (convIdForPrompt) {
      if (originalForLog) {
        await sbPost('messages', { conversation_id: convIdForPrompt, role: 'prompt_meta', content: originalForLog }, session.token);
        await sbPost('messages', { conversation_id: convIdForPrompt, role: 'prompt_meta', content: `✨ ${text}` }, session.token);
      } else {
        await sbPost('messages', { conversation_id: convIdForPrompt, role: 'prompt_meta', content: text }, session.token);
      }
    }

    let fullUserMessage = text;
    if (docs.length) {
      fullUserMessage = buildDocPrefix(docs) + text;
      setAttachedDocs([]);
    }
    if (notes.length > 0 && /\bnotes?\b/i.test(text)) {
      fullUserMessage += buildNotesContext();
    }

    const displayMsg = { role: 'user', content: docs.length ? `${docs.map(d => `[${d.name}]`).join(' ')} ${text}` : text };
    const newHistory = [...history, { role: 'user', content: fullUserMessage }];
    setHistory(newHistory);

    setMessages(prev => [...prev, displayMsg, {
      role: 'assistant', content: '⚖ Council is meeting…',
      modelLabel: 'The Council', modelSub: '',
    }]);

    const isFirstExchange = !currentConvId;
    const convId = await ensureConversation(text);
    await saveMessage(convId, 'user', fullUserMessage);

    const configured = [councilPrime, ...councilMembers.filter(id => id !== councilPrime)]
      .map(id => MODELS.find(m => m.id === id))
      .filter(Boolean);
    if (!configured.length) throw new Error('No Council members are configured. Open Settings > Council.');

    // Active modes aren't in newHistory (their activation phrase never became a
    // stored/visible message), so re-assert them for every council member on
    // every request while active, the same treatment as the single-model path.
    let outgoingHistory = newHistory;
    if (privateModeActive) outgoingHistory = [{ role: 'user', content: 'Snurglepuff' }, ...outgoingHistory];
    if (projectQActive)    outgoingHistory = [{ role: 'user', content: 'Beware of frogs' }, ...outgoingHistory];
    const contextHistory = buildContextWindow(outgoingHistory, sessionContext);

    const failures = [];      // { label, reason } for the Council notes
    const warnings = [];      // notices from answers that did arrive (truncated, search timeout)

    // Step 1: Prime answers. If the configured Prime fails, the next member takes
    // over as Prime so the Council still delivers an answer.
    let prime = null;
    let primeAnswer = '';
    let primeNotices = [];
    for (const cand of configured) {
      setCouncilStatus(`⚖ Council is meeting… ${cand.label} is drafting the first analysis.`);
      const r = await callModelFull(cand, contextHistory, sig);
      if (splitManifest(r.text)[0].trim()) { prime = cand; primeAnswer = r.text; primeNotices = r.notices; break; }
      failures.push({ label: cand.label, reason: (r.notices.map(noticeMessage).join(' ') || 'returned an empty answer') });
    }
    if (!prime) {
      const err = new Error('no member returned an answer');
      err.councilFailures = failures;
      throw err;
    }
    warnings.push(...primeNotices.map(n => ({ ...n, label: prime.label })));

    // Step 2: Other members review in parallel. Only add new info. They see the
    // Prime's analysis without its source manifest.
    const otherModels = configured.filter(m => m.id !== prime.id);
    const [primeBody] = splitManifest(primeAnswer);
    const memberHistory = [
      ...contextHistory,
      { role: 'assistant', content: primeBody },
      { role: 'user', content: 'You are a council member reviewing the above analysis. Do NOT restate or repeat any information already covered. Contribute ONLY: corrections to factual errors, additional angles or perspectives not yet addressed, important caveats or clarifications, or respectful disagreements with the reasoning. If you have nothing substantive to add beyond what is already stated, respond only with: "No additional insights."' },
    ];
    if (otherModels.length) setCouncilStatus(`⚖ Council is meeting… ${otherModels.map(m => m.label).join(', ')} reviewing.`);
    const additions = await Promise.all(otherModels.map(async m => {
      const r = await callModelFull(m, memberHistory, sig);
      const body = splitManifest(r.text)[0].trim();
      if (!body) { failures.push({ label: m.label, reason: (r.notices.map(noticeMessage).join(' ') || 'returned an empty answer') }); return null; }
      r.notices.filter(n => n.status !== 'info').forEach(n => warnings.push({ ...n, label: m.label }));
      return { label: m.label, text: body };
    }));
    const meaningful = additions.filter(a => a && !a.text.toLowerCase().startsWith('no additional'));

    // Step 3: Prime synthesizes all contributions. If synthesis fails, the
    // Prime's first answer is delivered instead of a blank.
    let finalAnswer = primeAnswer;
    if (meaningful.length > 0) {
      setCouncilStatus(`⚖ Council is meeting… ${prime.label} is synthesizing ${meaningful.length + 1} analyses.`);
      const synthesisHistory = [
        ...contextHistory,
        { role: 'assistant', content: primeBody },
        { role: 'user', content: `The following council members reviewed your analysis:\n\n${meaningful.map(a => `**${a.label}:** ${a.text}`).join('\n\n---\n\n')}\n\nSynthesize all valid contributions into one final, authoritative answer. Incorporate any corrections, additional insights, and important perspectives. Remove redundancy. Present as a single unified response without referencing this multi-step process.` },
      ];
      const s = await callModelFull(prime, synthesisHistory, sig);
      if (splitManifest(s.text)[0].trim()) {
        finalAnswer = s.text;
        s.notices.forEach(n => warnings.push({ ...n, label: prime.label }));
      } else {
        failures.push({ label: `${prime.label} (synthesis)`, reason: `${s.notices.map(noticeMessage).join(' ') || 'returned an empty answer'} The first analysis by ${prime.label} is shown instead.` });
      }
    }

    // Council notes: every failure and warning, named, above the source manifest.
    const noteLines = [];
    if (prime.id !== configured[0].id) noteLines.push(`${prime.label} served as Prime because ${configured[0].label} did not answer.`);
    failures.forEach(f => noteLines.push(`${f.label}: ${f.reason}`));
    warnings.forEach(w => noteLines.push(noticeMessage(w)));
    const uniqueNotes = [...new Set(noteLines)];
    if (uniqueNotes.length) {
      finalAnswer = insertBeforeManifest(finalAnswer, `\n\n> **Council notes**\n${uniqueNotes.map(l => `> - ${l}`).join('\n')}`);
    }

    const jurisdictions = detectJurisdictions(finalAnswer);
    const manifest = parseSourceManifest(finalAnswer);
    if (manifest) { setSourceManifest(manifest); setBottomTab('manifest'); }

    const participants = [prime, ...otherModels].map(m => m.label).join(' + ');
    let finalMsgIndex = 0;
    setMessages(prev => {
      const updated = [...prev]; finalMsgIndex = updated.length - 1;
      updated[finalMsgIndex] = {
        role: 'assistant', content: finalAnswer,
        modelLabel: 'The Council',
        modelSub: participants,
        jurisdictions, msgIndex: finalMsgIndex,
      };
      return updated;
    });

    setHistory(prev => [...prev, { role: 'assistant', content: finalAnswer }]);
    const savedMsgId = await saveMessage(convId, 'assistant', '[PM_MODEL:The Council|Council]' + finalAnswer);
    verifyCitations(finalMsgIndex, finalAnswer, convId, savedMsgId);
    if (isFirstExchange) generateConversationTitle(convId, text, finalAnswer);
    if (isFirstExchange) generateSessionContext(text, finalAnswer);

    } catch (err) {
      // Always replace the placeholder so a failure is visible, and offer Retry.
      const content = err.name === 'AbortError'
        ? '*[Council response interrupted]*'
        : err.councilFailures
          ? `⚠ **The Council could not answer.** No member returned an answer.\n\n${err.councilFailures.map(f => `- **${f.label}:** ${f.reason}`).join('\n')}\n\nUse **Retry** below to send the same request again.`
          : `⚠ **Council failed:** ${err.message}\n\nUse **Retry** below to send the same request again.`;
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant', content,
          modelLabel: 'The Council', modelSub: '',
          ...(err.name === 'AbortError' ? {} : { failed: true, retry: { text, docs } }),
        };
        return updated;
      });
    } finally {
      abortCtrlRef.current = null;
      setLoading(false);
    }
  }

  // ── Send query ─────────────────────────────────────────────────────────────
  async function sendQuery(overrideText, originalForLog, docsOverride) {
    const text = (overrideText != null ? overrideText : input).trim();
    if (!text || loading) return;

    if (text.toLowerCase() === 'beware of frogs') {
      setProjectQActive(true);
      setInput('');
      return;
    }

    if (text.toLowerCase() === 'snurglepuff') {
      setPrivateModeActive(true);
      setInput('');
      return;
    }

    if (councilMode && councilMembers.length >= 2 && councilPrime) return sendCouncilQuery(text, originalForLog, docsOverride);
    setInput('');
    setLoading(true);
    const docs = docsOverride || attachedDocs;

    setPromptHistory(prev => [...prev, text]);
    const convIdForPrompt = currentConvId || await ensureConversation(text);
    if (convIdForPrompt) {
      if (originalForLog) {
        await sbPost('messages', { conversation_id: convIdForPrompt, role: 'prompt_meta', content: originalForLog }, session.token);
        await sbPost('messages', { conversation_id: convIdForPrompt, role: 'prompt_meta', content: `✨ ${text}` }, session.token);
      } else {
        await sbPost('messages', { conversation_id: convIdForPrompt, role: 'prompt_meta', content: text }, session.token);
      }
    }

    let fullUserMessage = text;
    if (docs.length) {
      fullUserMessage = buildDocPrefix(docs) + text;
      setAttachedDocs([]);
    }
    if (notes.length > 0 && /\bnotes?\b/i.test(text)) {
      fullUserMessage += buildNotesContext();
    }

    const userMsg    = { role: 'user', content: fullUserMessage };
    const displayMsg = { role: 'user', content: docs.length ? `${docs.map(d => `[${d.name}]`).join(' ')} ${text}` : text };
    const newHistory = [...history, userMsg];
    setHistory(newHistory);

    // Active modes aren't in newHistory (their activation phrase never became a
    // stored/visible message), so re-assert them for the model on every request
    // while active. Not added to history/messages state or saved to Supabase.
    let outgoingHistory = newHistory;
    if (privateModeActive) outgoingHistory = [{ role: 'user', content: 'Snurglepuff' }, ...outgoingHistory];
    if (projectQActive)    outgoingHistory = [{ role: 'user', content: 'Beware of frogs' }, ...outgoingHistory];

    const assistantPlaceholder = {
      role: 'assistant', content: '', thinking: true,
      modelLabel: selectedModel.label, modelSub: selectedModel.sub,
    };
    setMessages(prev => [...prev, displayMsg, assistantPlaceholder]);

    const isFirstExchange = !currentConvId;
    const convId  = await ensureConversation(text);
    await saveMessage(convId, 'user', fullUserMessage);

    const fullRef = { current: '' };
    const renderThrottleRef = { current: 0 };
    const modelMeta = `[PM_MODEL:${selectedModel.label}|${selectedModel.sub}]`;

    // Pre-create assistant message so content is saved in real-time during streaming
    let streamMsgId = await saveMessage(convId, 'assistant', modelMeta + '…');
    const patchSaved = async (content) => {
      if (!streamMsgId) { streamMsgId = await saveMessage(convId, 'assistant', content); return; }
      try {
        await fetch(`${SB_URL}/rest/v1/messages?id=eq.${streamMsgId}`, {
          method: 'PATCH',
          headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}`, 'Content-Type': 'application/json', 'Prefer': 'return=minimal' },
          body: JSON.stringify({ content }),
        });
      } catch {}
    };
    let streamInterval = streamMsgId ? setInterval(() => {
      if (fullRef.current) patchSaved(modelMeta + fullRef.current);
    }, 4000) : null;

    const onDelta = (t) => {
      fullRef.current = t;
      const now = Date.now();
      if (now - renderThrottleRef.current < 80) return;
      renderThrottleRef.current = now;
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        updated[updated.length - 1] = { role: 'assistant', content: t, streaming: true, modelLabel: last.modelLabel, modelSub: last.modelSub };
        return updated;
      });
    };
    const requestInit = () => ({
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${selectedModel.apiKey || API_KEY}` },
      body: JSON.stringify({
        model: selectedModel.model,
        messages: buildContextWindow(outgoingHistory, sessionContext),
        stream: true,
        aperture,
        model_id: selectedModel.id,
        user_id: session?.user?.id || '',
        utility_model: UTILITY_MODELS.find(u => u.id === utilityConfig.modelId)?.model || 'llama-3.3-70b-versatile',
        utility_api_key: utilityConfig.apiKey || '',
        utility_url: utilityConfig.endpoint || 'https://api.groq.com/openai/v1',
      }),
    });

    abortCtrlRef.current = new AbortController();
    try {
      // One silent reconnect when the bridge is unreachable (tunnel blip).
      let result = await fetchBridge(selectedModel.url, requestInit(), abortCtrlRef.current.signal, onDelta, selectedModel.label);
      if (result.network && !result.text) {
        setMessages(prev => {
          const upd = [...prev];
          upd[upd.length - 1] = { ...upd[upd.length - 1], content: 'Reconnecting…', thinking: true };
          return upd;
        });
        result = await fetchBridge(selectedModel.url, requestInit(), abortCtrlRef.current.signal, onDelta, selectedModel.label);
      }
      clearInterval(streamInterval);
      fullRef.current = result.text;

      // Never a blank answer: no answer text means a named failure with Retry.
      if (!splitManifest(fullRef.current)[0].trim()) {
        const content = failureText(selectedModel.label, result.notices);
        setMessages(prev => {
          const upd = [...prev];
          const last = upd[upd.length - 1];
          upd[upd.length - 1] = { role: 'assistant', content, modelLabel: last.modelLabel, modelSub: last.modelSub, failed: true, retry: { text, docs } };
          return upd;
        });
        await patchSaved(modelMeta + content);
        return;
      }

      // Answer arrived: add any warnings (cut off, library search timed out) above the manifest.
      fullRef.current = insertBeforeManifest(fullRef.current, noticesNote(result.notices));

      const jurisdictions = detectJurisdictions(fullRef.current);
      const manifest      = parseSourceManifest(fullRef.current);
      if (manifest) {
        setSourceManifest(manifest);
        setBottomTab('manifest');
      }

      let finalMsgIndex = 0;
      setMessages(prev => {
        const updated  = [...prev];
        finalMsgIndex  = updated.length - 1;
        updated[finalMsgIndex] = {
          role:       'assistant',
          content:    fullRef.current,
          modelLabel: updated[finalMsgIndex].modelLabel,
          modelSub:   updated[finalMsgIndex].modelSub,
          jurisdictions,
          msgIndex:   finalMsgIndex,
          // Also attach to the message so it survives model switches
          ...(manifest ? { sourceManifest: manifest } : {}),
        };
        return updated;
      });

      setHistory(prev => [...prev, { role: 'assistant', content: fullRef.current }]);
      await patchSaved(modelMeta + fullRef.current);

      // Verify citations and persist results using correct index
      verifyCitations(finalMsgIndex, fullRef.current, convId, streamMsgId);
      if (isFirstExchange) generateConversationTitle(convId, text, fullRef.current);
      if (isFirstExchange) generateSessionContext(text, fullRef.current);

    } catch (err) {
      // fetchBridge only throws on a user abort: keep whatever was streamed.
      clearInterval(streamInterval);
      const partial = fullRef.current;
      setMessages(prev => {
        const updated  = [...prev];
        const msgIndex = updated.length - 1;
        if (updated[msgIndex] && partial) {
          updated[msgIndex] = {
            role:       'assistant',
            content:    partial + '\n\n*[Response interrupted]*',
            modelLabel: updated[msgIndex].modelLabel,
            modelSub:   updated[msgIndex].modelSub,
            jurisdictions: detectJurisdictions(partial),
            msgIndex,
          };
        } else {
          updated[msgIndex] = { role: 'assistant', content: '*[Response interrupted]*' };
        }
        return updated;
      });
      if (partial) await patchSaved(modelMeta + partial + '\n\n*[Response interrupted]*');
    } finally {
      clearInterval(streamInterval);
      abortCtrlRef.current = null;
      setLoading(false);
    }
  }

  function retryMessage(m) {
    if (!m?.retry || loading) return;
    sendQuery(m.retry.text, null, m.retry.docs || []);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendQuery(); }
  }

  function handleTextareaInput(e) {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  }

  function handleModelChange(e) {
    if (loading) return;
    setSelectedModel(allModels.find(m => m.id === e.target.value));
  }

  // ── Case tracker ───────────────────────────────────────────────────────────
  async function loadProjects() {
    setCasesLoading(true);
    try {
      const rows = await sbGet(
        'projects',
        { user_id: `eq.${session.user.id}`, select: 'id,name,case_number,color,created_at', order: 'created_at.desc' },
        session.token
      );
      if (Array.isArray(rows)) {
        setCases(rows.map(r => ({ id: r.id, name: r.name, case_number: r.case_number, color: r.color || '#2196F3', created: new Date(r.created_at).toLocaleDateString() })));
      }
    } catch (e) { console.error('loadProjects:', e); }
    setCasesLoading(false);
  }

  async function addCase() {
    if (!newCaseName.trim()) return;
    try {
      const rows = await sbPost('projects', { user_id: session.user.id, name: newCaseName.trim() }, session.token);
      if (Array.isArray(rows) && rows[0]) {
        setCases(prev => [{ id: rows[0].id, name: rows[0].name, case_number: rows[0].case_number, color: rows[0].color || '#2196F3', created: new Date(rows[0].created_at).toLocaleDateString() }, ...prev]);
      }
    } catch (e) { console.error('addCase:', e); }
    setNewCaseName('');
  }

  async function deleteCase(projectId) {
    try {
      await fetch(`${SB_URL}/rest/v1/projects?id=eq.${projectId}`, {
        method: 'DELETE',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}` }
      });
      setCases(prev => prev.filter(c => c.id !== projectId));
    } catch (e) { console.error('deleteCase:', e); }
  }

  async function updateCaseColor(projectId, color) {
    setCases(prev => prev.map(c => c.id === projectId ? { ...c, color } : c));
    try {
      await fetch(`${SB_URL}/rest/v1/projects?id=eq.${projectId}`, {
        method: 'PATCH',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ color })
      });
    } catch (e) { console.error('updateCaseColor:', e); }
  }

  async function linkCaseToConv(projectId) {
    if (!currentConvId) return;
    try {
      await fetch(`${SB_URL}/rest/v1/conversations?id=eq.${currentConvId}`, {
        method: 'PATCH',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId })
      });
    } catch (e) { console.error('linkCaseToConv:', e); }
  }

  // ── Key Dates + Google Calendar sync ──────────────────────────────────────
  const PROJECT_COLOR_TO_CALENDAR_COLOR_ID = {
    '#2196F3': '9',  // Blueberry
    '#E53935': '11', // Tomato
    '#43A047': '10', // Basil
    '#FB8C00': '6',  // Tangerine
    '#8E24AA': '3',  // Grape
    '#00ACC1': '7',  // Peacock
  };

  async function loadProjectEvents(projectId) {
    try {
      const rows = await sbGet(
        'project_events',
        { project_id: `eq.${projectId}`, select: 'id,title,event_date', order: 'event_date.asc' },
        session.token
      );
      if (Array.isArray(rows)) {
        setProjectEvents(prev => ({ ...prev, [projectId]: rows }));
      }
    } catch (e) { console.error('loadProjectEvents:', e); }
  }

  async function syncEventToGoogleCalendar(title, eventDate, projectColor) {
    const googleToken = localStorage.getItem('pm_google_token');
    if (!googleToken) return;
    const colorId = PROJECT_COLOR_TO_CALENDAR_COLOR_ID[projectColor] || '9';
    try {
      await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${googleToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          summary: title,
          start: { date: eventDate },
          end: { date: eventDate },
          colorId,
        })
      });
    } catch (e) { console.error('syncEventToGoogleCalendar:', e); }
  }

  async function addProjectEvent(projectId, projectColor) {
    if (!newEventTitle.trim() || !newEventDate) return;
    try {
      const rows = await sbPost('project_events', { project_id: projectId, user_id: session.user.id, title: newEventTitle.trim(), event_date: newEventDate }, session.token);
      if (Array.isArray(rows) && rows[0]) {
        setProjectEvents(prev => ({ ...prev, [projectId]: [...(prev[projectId] || []), rows[0]].sort((a, b) => a.event_date.localeCompare(b.event_date)) }));
      }
      syncEventToGoogleCalendar(newEventTitle.trim(), newEventDate, projectColor);
    } catch (e) { console.error('addProjectEvent:', e); }
    setNewEventTitle('');
    setNewEventDate('');
  }

  async function deleteProjectEvent(projectId, eventId) {
    try {
      await fetch(`${SB_URL}/rest/v1/project_events?id=eq.${eventId}`, {
        method: 'DELETE',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}` }
      });
      setProjectEvents(prev => ({ ...prev, [projectId]: (prev[projectId] || []).filter(e => e.id !== eventId) }));
    } catch (e) { console.error('deleteProjectEvent:', e); }
  }

  function toggleExpandCase(projectId) {
    const next = expandedCaseId === projectId ? null : projectId;
    setExpandedCaseId(next);
    if (next && !projectEvents[next]) loadProjectEvents(next);
  }

  // ── Markdown renderer (no external lib) ───────────────────────────────────
  async function saveNote(note) {
    if (!currentConvId) return;
    if (note.id) {
      await fetch(`${SB_URL}/rest/v1/notes?id=eq.${note.id}`, {
        method: 'PATCH',
        headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: note.text })
      });
      setNotes(prev => prev.map(n => n.id === note.id ? { ...n, text: note.text } : n));
    } else {
      const body = { conversation_id: currentConvId, msg_index: note.msgIndex, block_id: note.blockId, text: note.text };
      const data = await sbPost('notes', body, session.token);
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.id) setNotes(prev => [...prev, { ...body, id: row.id, created_at: row.created_at }]);
    }
  }

  function resolveNoteAnchorText(note) {
    const msg = messages[note.msg_index];
    if (!msg) return '';
    const m = /^block-\d+-(\d+)$/.exec(note.block_id || '');
    if (!m) return '';
    const lines = (msg.content || '').split('\n');
    return (lines[Number(m[1])] || '').replace(/^#{1,3}\s*/, '').trim();
  }

  function buildNotesContext() {
    if (!notes.length) return '';
    const compiled = notes
      .slice()
      .sort((a, b) => a.msg_index - b.msg_index)
      .map((n, idx) => `${idx + 1}. [Pinned to: "${resolveNoteAnchorText(n)}"]\n   Note: ${n.text}`)
      .join('\n\n');
    return `\n\n---\nUSER'S PINNED NOTES FROM THIS SESSION:\n${compiled}\n---`;
  }

  async function deleteNote(noteId) {
    await fetch(`${SB_URL}/rest/v1/notes?id=eq.${noteId}`, {
      method: 'DELETE',
      headers: { 'apikey': SB_KEY, 'Authorization': `Bearer ${session.token}`, 'Content-Type': 'application/json' }
    });
    setNotes(prev => prev.filter(n => n.id !== noteId));
  }

  function handleContextMenu(e, msgIndex, blockId) {
    e.preventDefault();
    const sel = window.getSelection();
    const selectedText = sel && !sel.isCollapsed ? sel.toString() : '';
    setContextMenu({ x: e.clientX, y: e.clientY, msgIndex, blockId, selectedText });
  }

  function openNoteEditor(msgIndex, blockId, existingNote) {
    setContextMenu(null);
    const el = document.getElementById(blockId);
    const rect = el ? el.getBoundingClientRect() : { left: 400, bottom: 200 };
    setActiveNote({
      id: existingNote?.id || null,
      msgIndex,
      blockId,
      text: existingNote?.text || '',
      x: rect.left,
      y: rect.bottom + window.scrollY + 4,
    });
  }

  function closeNoteEditor() {
    if (activeNote && activeNote.text.trim()) {
      saveNote(activeNote);
    }
    setActiveNote(null);
  }

  function jumpToBlock(blockId) {
    const el = document.getElementById(blockId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setNoteHighlight(blockId);
    setTimeout(() => setNoteHighlight(null), 2000);
  }

  function renderMarkdown(text, msgIndex) {
    const verified = verifiedCitations[msgIndex] || new Set();

    // Inject checkmarks into citations before rendering (once per citation)
    if (verified.size > 0) {
      verified.forEach(citation => {
        // Only replace first occurrence to prevent double checkmarks
        const idx = text.indexOf(citation);
        if (idx !== -1) {
          text = text.slice(0, idx + citation.length) + ' [VERIFIED]' + text.slice(idx + citation.length);
        }
      });
    }

    // Collapse blank lines between ordered list blocks to prevent visual gaps
    const rawLines = text.split('\n');
    const collapsedLines = [];
    for (let ci = 0; ci < rawLines.length; ci++) {
      const line = rawLines[ci];
      if (line.trim() === '' &&
          ci > 0 && /^\d+\. /.test(rawLines[ci - 1]) &&
          ci < rawLines.length - 1 && /^\d+\. /.test(rawLines[ci + 1])) {
        continue;
      }
      collapsedLines.push(line);
    }

    const lines = collapsedLines;
    const elements = [];
    let i = 0;

    function parseInline(str) {
      // Process inline: bold, italic, code, citations with checkmarks
      const parts = [];
      let remaining = str;
      let key = 0;

      while (remaining.length > 0) {
      // Verified citation marker
      if (remaining.includes('[VERIFIED]')) {
        const idx = remaining.indexOf('[VERIFIED]');
        if (idx > 0) parts.push(<span key={key++}>{remaining.slice(0, idx)}</span>);
        parts.push(
          <span key={key++} style={{ color: 'var(--blue)', fontWeight: 700, fontSize: '0.9em', marginLeft: '3px' }} title="Verified citation">✓</span>
        );
        remaining = remaining.slice(idx + 10);
        continue;
      }
        // Bold **text**
        const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*/s);
        // Italic *text* (not **)
        const italicMatch = remaining.match(/^(.*?)(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/s);
        // Inline code `text`
        const codeMatch = remaining.match(/^(.*?)`(.+?)`/s);

        const candidates = [
          boldMatch   ? { type: 'bold',   match: boldMatch,   group: 2, prefix: boldMatch[1] }   : null,
          italicMatch ? { type: 'italic', match: italicMatch, group: 2, prefix: italicMatch[1] } : null,
          codeMatch   ? { type: 'code',   match: codeMatch,   group: 2, prefix: codeMatch[1] }   : null,
        ].filter(Boolean);

        if (candidates.length === 0) {
          parts.push(<span key={key++}>{remaining}</span>);
          break;
        }

        // Pick earliest match
        const earliest = candidates.reduce((a, b) =>
          a.prefix.length <= b.prefix.length ? a : b
        );

        if (earliest.prefix.length > 0) {
          parts.push(<span key={key++}>{earliest.prefix}</span>);
        }

        const inner = earliest.match[earliest.group];
        if (earliest.type === 'bold') {
          parts.push(<strong key={key++}>{inner}</strong>);
          remaining = remaining.slice(earliest.prefix.length + inner.length + 4);
        } else if (earliest.type === 'italic') {
          parts.push(<em key={key++}>{inner}</em>);
          remaining = remaining.slice(earliest.prefix.length + inner.length + 2);
        } else if (earliest.type === 'code') {
          parts.push(<code key={key++} style={{ background: 'var(--surface2)', color: 'var(--navy)', padding: '1px 5px', borderRadius: '3px', fontSize: '0.9em', fontFamily: 'monospace' }}>{inner}</code>);
          remaining = remaining.slice(earliest.prefix.length + inner.length + 2);
        }
      }

      return parts;
    }

    while (i < lines.length) {
      const line = lines[i];

      // Blank line
      if (line.trim() === '') {
        elements.push(<div key={i} style={{ height: '0.6em' }} />);
        i++;
        continue;
      }

      // Horizontal rule
      if (/^[-*_]{3,}$/.test(line.trim())) {
        elements.push(<hr key={i} style={{ border: 'none', borderTop: '0.5px solid var(--border)', margin: '0.5em 0' }} />);
        i++;
        continue;
      }

      // H1
      if (line.startsWith('# ')) {
        const h1Id = `block-${msgIndex}-${i}`;
        const h1Note = notes.find(n => n.block_id === h1Id);
        elements.push(<h1 key={i} id={h1Id} onContextMenu={e => handleContextMenu(e, msgIndex, h1Id)}
          style={{ fontSize: '1.1rem', fontWeight: 500, color: 'var(--navy)', margin: '0.6em 0 0.2em', position:'relative', cursor:'text',
            background: noteHighlight === h1Id ? 'rgba(33,150,243,0.1)' : 'transparent', borderRadius:'3px', transition:'background 0.8s' }}>
          {parseInline(line.slice(2))}{h1Note && <span onClick={() => openNoteEditor(msgIndex, h1Id, h1Note)} title={h1Note.text} style={{cursor:'pointer',marginLeft:'6px',fontSize:'0.75rem',color:'var(--blue)',userSelect:'none'}}>📌</span>}
        </h1>);
        i++;
        continue;
      }

      // H2
      if (line.startsWith('## ')) {
        const h2Id = `block-${msgIndex}-${i}`;
        const h2Note = notes.find(n => n.block_id === h2Id);
        elements.push(<h2 key={i} id={h2Id} onContextMenu={e => handleContextMenu(e, msgIndex, h2Id)}
          style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--navy)', margin: '0.5em 0 0.2em', position:'relative', cursor:'text',
            background: noteHighlight === h2Id ? 'rgba(33,150,243,0.1)' : 'transparent', borderRadius:'3px', transition:'background 0.8s' }}>
          {parseInline(line.slice(3))}{h2Note && <span onClick={() => openNoteEditor(msgIndex, h2Id, h2Note)} title={h2Note.text} style={{cursor:'pointer',marginLeft:'6px',fontSize:'0.75rem',color:'var(--blue)',userSelect:'none'}}>📌</span>}
        </h2>);
        i++;
        continue;
      }

      // H3
      if (line.startsWith('### ')) {
        const h3Id = `block-${msgIndex}-${i}`;
        const h3Note = notes.find(n => n.block_id === h3Id);
        elements.push(<h3 key={i} id={h3Id} onContextMenu={e => handleContextMenu(e, msgIndex, h3Id)}
          style={{ fontSize: '0.95rem', fontWeight: 500, color: 'var(--navy-mid)', margin: '0.4em 0 0.15em', position:'relative', cursor:'text',
            background: noteHighlight === h3Id ? 'rgba(33,150,243,0.1)' : 'transparent', borderRadius:'3px', transition:'background 0.8s' }}>
          {parseInline(line.slice(4))}{h3Note && <span onClick={() => openNoteEditor(msgIndex, h3Id, h3Note)} title={h3Note.text} style={{cursor:'pointer',marginLeft:'6px',fontSize:'0.75rem',color:'var(--blue)',userSelect:'none'}}>📌</span>}
        </h3>);
        i++;
        continue;
      }

      // Code block ```
      if (line.startsWith('```')) {
        const codeLines = [];
        i++;
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        elements.push(
          <pre key={i} style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: '4px', padding: '0.6em 0.8em', fontSize: '0.82rem', overflowX: 'auto', margin: '0.4em 0', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text)' }}>
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        i++;
        continue;
      }

      // Table (pipe-delimited)
      if (line.includes('|') && line.trim().startsWith('|')) {
        const tableLines = [];
        while (i < lines.length && lines[i].includes('|') && lines[i].trim().startsWith('|')) {
          tableLines.push(lines[i]);
          i++;
        }
        const rows = tableLines
          .filter(l => !/^\|[-| :]+\|$/.test(l.trim()))
          .map(l => l.split('|').filter((_, ci) => ci > 0 && ci < l.split('|').length - 1).map(c => c.trim()));
        if (rows.length > 0) {
          elements.push(
            <table key={i} style={{ borderCollapse: 'collapse', width: '100%', margin: '0.5em 0', fontSize: '0.88rem' }}>
              <thead>
                <tr>{rows[0].map((cell, ci) => (
                  <th key={ci} style={{ textAlign: 'left', padding: '0.3em 0.6em', borderBottom: '1.5px solid var(--border)', color: 'var(--navy)', fontWeight: 500 }}>{parseInline(cell)}</th>
                ))}</tr>
              </thead>
              <tbody>
                {rows.slice(1).map((row, ri) => (
                  <tr key={ri} style={{ borderBottom: '0.5px solid var(--border)' }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ padding: '0.3em 0.6em', color: 'var(--text)' }}>{parseInline(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
        continue;
      }

      // Unordered list
      if (/^[-*+] /.test(line)) {
        const listItems = [];
        const seen = new Set();
        while (i < lines.length && /^[-*+] /.test(lines[i])) {
          const item = lines[i].replace(/^[-*+] /, '');
          if (!seen.has(item)) { listItems.push(item); seen.add(item); }
          i++;
        }
        elements.push(
          <ul key={i} style={{ paddingLeft: '1.8em', margin: '0.2em 0' }}>
            {listItems.map((item, li) => (
              <li key={li} style={{ color: 'var(--text)', lineHeight: 1.7, marginBottom: '0.1em' }}>{parseInline(item)}</li>
            ))}
          </ul>
        );
        continue;
      }

      // Ordered list
      if (/^\d+\. /.test(line)) {
        const listItems = [];
        const seen = new Set();
        while (i < lines.length && /^\d+\. /.test(lines[i])) {
          const item = lines[i].replace(/^\d+\. /, '');
          if (!seen.has(item)) { listItems.push(item); seen.add(item); }
          i++;
        }
        elements.push(
          <ol key={i} style={{ paddingLeft: '2.2em', margin: '0.2em 0' }}>
            {listItems.map((item, li) => (
              <li key={li} style={{ color: 'var(--text)', lineHeight: 1.7, marginBottom: '0.1em' }}>{parseInline(item)}</li>
            ))}
          </ol>
        );
        continue;
      }

      // Blockquote
      if (line.startsWith('> ')) {
        elements.push(
          <blockquote key={i} style={{ borderLeft: '3px solid var(--blue)', paddingLeft: '0.8em', margin: '0.3em 0', color: 'var(--text-dim)', fontStyle: 'italic' }}>
            {parseInline(line.slice(2))}
          </blockquote>
        );
        i++;
        continue;
      }

      // Regular paragraph
      const pId = `block-${msgIndex}-${i}`;
      const pNote = notes.find(n => n.block_id === pId);
      elements.push(
        <p key={i} id={pId} onContextMenu={e => handleContextMenu(e, msgIndex, pId)}
          style={{ margin: '0.1em 0', lineHeight: 1.75, color: 'var(--text)', position: 'relative', cursor: 'text',
            background: noteHighlight === pId ? 'rgba(33,150,243,0.1)' : 'transparent',
            transition: 'background 0.8s', borderRadius: '3px' }}>
          {parseInline(line)}
          {pNote && <span onClick={() => openNoteEditor(msgIndex, pId, pNote)} title={pNote.text}
            style={{cursor:'pointer',marginLeft:'6px',fontSize:'0.75rem',color:'var(--blue)',verticalAlign:'middle',userSelect:'none'}}>📌</span>}
        </p>
      );
      i++;
    }

    return <div className="msg-body">{elements}</div>;
  }

  function renderContent(content, msgIndex, streaming) {
    // Strip SOURCE MANIFEST block from response -- shown in Sources panel instead
    content = content.replace(/\n?---\n\*\*SOURCE MANIFEST\*\*[\s\S]*?---\n?/gi, '');
    content = content.replace(/\n?\*\*SOURCE MANIFEST\*\*[\s\S]*?---\n?/gi, '');
    content = content.replace(/\n?SOURCE MANIFEST[\s\S]*?---\n?/gi, '');
    if (streaming) {
      return (
        <pre style={{
          fontFamily: 'inherit',
          fontSize: 'inherit',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          margin: 0,
          color: 'var(--text)',
          lineHeight: 1.75,
        }}>
          {content}
        </pre>
      );
    }
    return renderMarkdown(content, msgIndex);
  }

  // ── Drive backup: separate OAuth flow, independent of the Canjur login ────
  function handleConnectBackupDrive() {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      alert('Google Drive backup is not configured (missing client ID in this deployment). Contact support.');
      return;
    }
    clearBackupSyncCache(); // avoid reusing a prior (possibly different-account) folder/file cache
    const redirectUri = 'https://app.wavecanon.com/auth/google-drive/callback';
    const scope       = 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/userinfo.email';
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(scope)}` +
      `&access_type=offline&prompt=consent%20select_account`;
    window.location.href = authUrl;
  }

  function handleDisconnectBackupDrive() {
    disconnectBackup();
    setBackupConnectedEmail('');
    setBackupConnected(false);
    setBackupEnabled(false);
    localStorage.setItem('pm_backup_enabled', 'false');
    setBackupStatus('idle');
  }

  function handleToggleBackupEnabled(checked) {
    setBackupEnabled(checked);
    localStorage.setItem('pm_backup_enabled', checked ? 'true' : 'false');
  }

  function handleBackupFrequencyChange(minutes) {
    const clamped = Math.min(20, Math.max(1, minutes || 1));
    setBackupFrequencyMin(clamped);
    localStorage.setItem('pm_backup_frequency_min', String(clamped));
  }

  // ── Chat copy / print helpers ───────────────────────────────────────────
  function buildChatText() {
    return buildChatTextFromRows(messages, sourceManifest, selectedModel.label);
  }

  function buildSourcesText() {
    if (!sourceManifest) return 'No sources available.';
    const lines = [];
    if (sourceManifest.corpus.length > 0) {
      lines.push('CORPUS SOURCES');
      sourceManifest.corpus.forEach(s => lines.push('  ' + s));
    }
    if (sourceManifest.live.length > 0) {
      lines.push('LIVE SOURCES');
      sourceManifest.live.forEach(s => lines.push('  ' + s));
    }
    return lines.join('\n');
  }

  function handleCopyChat() {
    navigator.clipboard.writeText(buildChatText()).catch(() => {});
  }

  function handlePrintChat() {
    const win = window.open('', '_blank');
    const chatHtml = messages.map(m => {
      if (m.role === 'user') return `<div class="pm-role">YOU</div><div class="pm-content">${m.content.replace(/\n/g, '<br/>')}</div><hr/>`;
      if (m.role === 'assistant') {
        const label = m.modelLabel ? `${m.modelLabel} · ${m.modelSub}` : selectedModel.label;
        return `<div class="pm-role">${label}</div><div class="pm-content">${m.content.replace(/\n/g, '<br/>')}</div><hr/>`;
      }
      return '';
    }).join('');
    let sourcesHtml = '';
    if (sourceManifest) {
      sourcesHtml = '<h2>Sources</h2>';
      if (sourceManifest.corpus.length > 0) {
        sourcesHtml += '<h3>Corpus</h3><ul>' + sourceManifest.corpus.map(s => `<li>${s}</li>`).join('') + '</ul>';
      }
      if (sourceManifest.live.length > 0) {
        sourcesHtml += '<h3>Live</h3><ul>' + sourceManifest.live.map(s => `<li>${s}</li>`).join('') + '</ul>';
      }
    }
    win.document.write(`<!DOCTYPE html><html><head><title>Canjur: Research Session</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #1A2A3A; padding: 2rem; max-width: 860px; margin: 0 auto; }
        .pm-role { font-size: 0.75rem; letter-spacing: 0.12em; text-transform: uppercase; color: #6B7F96; margin-top: 1rem; }
        .pm-content { margin: 0.3rem 0 0.8rem; line-height: 1.7; white-space: pre-wrap; }
        hr { border: none; border-top: 1px solid #D0DCE8; margin: 0.5rem 0; }
        h2 { font-size: 0.9rem; letter-spacing: 0.1em; text-transform: uppercase; color: #0A1628; margin-top: 2rem; }
        h3 { font-size: 0.8rem; color: #6B7F96; margin: 0.8rem 0 0.3rem; }
        ul { padding-left: 1.5rem; }
        li { margin-bottom: 0.2rem; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <h1 style="font-size:1rem;letter-spacing:0.15em;text-transform:uppercase;color:#0A1628;margin-bottom:1.5rem;">Canjur: Research Session</h1>
      ${chatHtml}${sourcesHtml}
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  }

  function handleCopySources() {
    navigator.clipboard.writeText(buildSourcesText()).catch(() => {});
  }

  function handlePrintSources() {
    const win = window.open('', '_blank');
    let html = '<h2>Corpus</h2><ul>' + (sourceManifest?.corpus || []).map(s => `<li>${s}</li>`).join('') + '</ul>';
    if (sourceManifest?.live?.length > 0) {
      html += '<h2>Live</h2><ul>' + sourceManifest.live.map(s => `<li>${s}</li>`).join('') + '</ul>';
    }
    win.document.write(`<!DOCTYPE html><html><head><title>Canjur: Sources</title>
      <style>body{font-family:'Segoe UI',Arial,sans-serif;font-size:13px;padding:2rem;max-width:700px;margin:0 auto;}h2{font-size:0.85rem;letter-spacing:0.1em;text-transform:uppercase;color:#0A1628;margin:1.5rem 0 0.5rem;}ul{padding-left:1.5rem;}li{margin-bottom:0.25rem;line-height:1.6;}</style>
      </head><body><h1 style="font-size:1rem;letter-spacing:0.15em;text-transform:uppercase;color:#0A1628;margin-bottom:1.5rem;">Canjur: Sources</h1>${html}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  }

  function buildPromptsText() {
    return promptHistory.join('\n\n');
  }

  function handleCopyPrompts() {
    navigator.clipboard.writeText(buildPromptsText()).catch(() => {});
  }

  function handlePrintPrompts() {
    const win = window.open('', '_blank');
    const html = promptHistory.map((p, i) => `<div class="prompt"><span class="num">${i+1}.</span> ${p}</div>`).join('');
    win.document.write(`<!DOCTYPE html><html><head><title>Canjur: Prompts</title>
      <style>body{font-family:'Segoe UI',Arial,sans-serif;font-size:13px;padding:2rem;max-width:700px;margin:0 auto;}.prompt{margin-bottom:1rem;line-height:1.6;}.num{color:#6B7F96;margin-right:0.4rem;}</style>
      </head><body><h1 style="font-size:1rem;letter-spacing:0.15em;text-transform:uppercase;color:#0A1628;margin-bottom:1.5rem;">Canjur: Prompts</h1>${html}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className={`app ${dragOver ? 'drag-over' : ''}`}
      data-theme={theme}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragOver && (
        <div className="drop-overlay">
          <div className="drop-message">Drop document to attach</div>
        </div>
      )}

      <NavRail
        sidebarOpen={sidebarOpen}
        onNewChat={async () => { await refreshConversations(); startNewChat(); setSidebarOpen(false); }}
        onToggleHistory={() => { setSidebarCollapsed(false); setSidebarOpen(!sidebarOpen); }}
        onOpenProjects={() => { setCaseTrackerOpen(!caseTrackerOpen); setSidebarOpen(false); }}
        onToggleCouncil={() => { if (councilMembers.length >= 2 && councilPrime) setCouncilMode(m => !m); setSidebarOpen(false); }}
        councilDisabled={councilMembers.length < 2 || !councilPrime}
        onOpenCode={() => { setCodePanelOpen(!codePanelOpen); setSidebarOpen(false); }}
        onOpenSettings={() => { setSettingsOpen(!settingsOpen); setCaseTrackerOpen(false); setSidebarOpen(false); }}
        onOpenHelp={() => { setHelpOpen(!helpOpen); setSidebarOpen(false); }}
        onOpenAccount={() => { setSettingsOpen(true); setCaseTrackerOpen(false); setSidebarOpen(false); }}
        onOpenPromptOptimizer={() => { setPromptOptimizerOpen(!promptOptimizerOpen); setSidebarOpen(false); }}
      />

      {/* ── Header ── */}
      <div className="app-header">
        <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
          <span></span><span></span><span></span>
        </button>
        <span className="app-brand">
          <span className="app-brand-mark" aria-hidden="true" />
          <span className="app-brand-wordmark" role="img" aria-label="Canjur" />
        </span>

        {/* Focus Aperture toggle */}
        <div className="aperture-toggle" title="Focus Aperture">
          <span className={`aperture-label ${aperture === 'legal' ? 'aperture-label--active' : ''}`}>Legal/Fin</span>
          <button
            className={`aperture-switch ${aperture === 'open' ? 'aperture-switch--open' : ''}`}
            onClick={() => setAperture(a => a === 'legal' ? 'open' : 'legal')}
            title={aperture === 'legal' ? 'Switch to Open scope' : 'Switch to Legal/Fin scope'}
          >
            <span className="aperture-knob" />
          </button>
          <span className={`aperture-label ${aperture === 'open' ? 'aperture-label--active' : ''}`}>Open</span>
        </div>

        {/* Chat copy/print buttons */}
        <div className="chat-actions">
          <button className="chat-action-btn" onClick={handleCopyChat} title="Copy entire chat + sources to clipboard">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
            </svg>
          </button>
          <button className="chat-action-btn" onClick={handlePrintChat} title="Print chat + sources">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
            </svg>
          </button>
          <button className="chat-action-btn" onClick={() => setExportMenuOpen(exportMenuOpen === 'chat' ? null : 'chat')} title="Export chat + sources">
            ⬇
          </button>
          {exportMenuOpen === 'chat' && (
            <ExportMenu
              onExport={(fmt) => exportContent(fmt, buildChatText(), 'canjur-chat', 'Canjur: Research Session')}
              onClose={() => setExportMenuOpen(null)}
            />
          )}
        </div>

        <div className="model-selector">
          <select
            className={`model-dropdown ${loading ? 'model-dropdown--locked' : ''}`}
            value={selectedModel.id}
            onChange={handleModelChange}
            disabled={loading}
            title={loading ? 'Cannot switch model while a response is in progress' : ''}
          >
            {allModels.map(m => {
              const hasKey = m.free || !!userKeys[m.id];
              return (
                <option key={m.id} value={m.id} disabled={!hasKey}
                  style={{ color: hasKey ? 'inherit' : '#aaa' }}>
                  {m.label} · {m.sub}{!hasKey ? ' (add key)' : ''}
                </option>
              );
            })}
          </select>
          {loading && <span className="model-lock-indicator">⏳</span>}
          {selectedModel.apiRequired && (
            <button
              className="model-key-btn"
              title={userKeys[selectedModel.id] ? 'Edit API key' : 'Add API key'}
              onClick={() => { setFocusSettingsModel(selectedModel.id); setSettingsOpen(true); setCaseTrackerOpen(false); }}
              style={{background:'none',border:'none',cursor:'pointer',fontSize:'0.75rem',
                color: userKeys[selectedModel.id] ? '#2196F3' : '#e67e22', padding:'0 2px'}}
            >
              {userKeys[selectedModel.id] ? '✏️' : '＋'}
            </button>
          )}
        </div>

        <button
          className={`council-pill${councilMode ? ' council-pill--active' : ''}${councilMembers.length < 2 || !councilPrime ? ' council-pill--disabled' : ''}`}
          onClick={() => { if (councilMembers.length >= 2 && councilPrime) setCouncilMode(m => !m); }}
          title={councilMembers.length >= 2 && councilPrime ? (councilMode ? 'Switch to single-model mode' : 'Switch to Council mode') : 'Configure Council in Settings (need 2+ members + Prime)'}
        >
          Council
        </button>

        <span className={`q-badge${projectQActive ? ' q-badge--active' : ''}`}>Q</span>

        <span className={`crown-badge${privateModeActive ? ' crown-badge--active' : ''}`}>
          <svg viewBox="0 0 24 20" width="20" height="17" fill="currentColor" aria-hidden="true">
            <polygon points="2,15 2,6 6,9 12,2 18,9 22,6 22,15" />
            <rect x="2" y="15" width="20" height="3" rx="1" />
          </svg>
        </span>

        <span className="app-header-status">
          <span className="status-dot"></span>Live
          <button className="case-tracker-btn" onClick={() => setCaseTrackerOpen(!caseTrackerOpen)} title="Projects">⚖</button>
          <button className="case-tracker-btn" onClick={() => { setSettingsOpen(true); setCaseTrackerOpen(false); }} title="Settings">⚙</button>
          <button className="signout-btn" onClick={onLogout}>Sign out</button>
        </span>
      </div>

      {/* ── Settings Panel ── */}
      {settingsOpen && <div style={{position:'fixed',inset:0,zIndex:199}} onClick={() => { setSettingsOpen(false); setFocusSettingsModel(null); setFocusSettingsTab(null); }} />}
      {settingsOpen && (
        <SettingsPanel
          onClose={() => { setSettingsOpen(false); setFocusSettingsModel(null); setFocusSettingsTab(null); }}
          initialTab={focusSettingsTab}
          userKeys={userKeys}
          onSaveKey={saveUserKey}
          onDeleteKey={deleteUserKey}
          utilityConfig={utilityConfig}
          onSaveUtility={saveUtilityConfig}
          defaultModelId={defaultModelId}
          onSaveDefaultModel={(id) => {
            setDefaultModelId(id);
            localStorage.setItem('pm_default_model', id);
            const m = (allModels || MODELS).find(x => x.id === id);
            if (m) setSelectedModel(m);
          }}
          focusModelId={focusSettingsModel}
          session={session}
          councilMembers={councilMembers}
          councilPrime={councilPrime}
          onSaveCouncil={(members, prime) => { setCouncilMembers(members); setCouncilPrime(prime); if (members.length < 2 || !prime) setCouncilMode(false); }}
          allModels={allModels}
          customModels={customModels}
          onCustomModelsChange={saveCustomModels}
          theme={theme}
          onThemeChange={setTheme}
          backupEnabled={backupEnabled}
          backupFrequencyMin={backupFrequencyMin}
          backupConnectedEmail={backupConnectedEmail}
          backupConnected={backupConnected}
          backupStatus={backupStatus}
          backupLastSyncedAt={backupLastSyncedAt}
          onConnectBackupDrive={handleConnectBackupDrive}
          onDisconnectBackupDrive={handleDisconnectBackupDrive}
          onToggleBackupEnabled={handleToggleBackupEnabled}
          onBackupFrequencyChange={handleBackupFrequencyChange}
        />
      )}

      {/* ── Help Bubble ── */}
      {helpOpen && <HelpBubble onClose={() => setHelpOpen(false)} />}
      {promptOptimizerOpen && (
        <PromptOptimizerModal
          initialText={input}
          onOptimize={optimizePromptText}
          onSend={handlePromptOptimizerSend}
          onClose={() => setPromptOptimizerOpen(false)}
        />
      )}

      {/* ── Code Panel ── */}
      {codePanelOpen && <CodePanel onClose={() => setCodePanelOpen(false)} />}

      {/* ── Case Tracker Panel ── */}
      {caseTrackerOpen && (
        <div className="case-tracker-panel">
          <div className="case-tracker-header">
            <span>Projects</span>
            <button onClick={() => setCaseTrackerOpen(false)}>✕</button>
          </div>
          <div className="case-tracker-body">
            <div className="case-add-row">
              <input
                className="case-name-input"
                placeholder="New case name..."
                value={newCaseName}
                onChange={e => setNewCaseName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCase()}
              />
              <button className="case-add-btn" onClick={addCase}>Add</button>
            </div>
            {casesLoading && <div className="case-empty">Loading projects...</div>}
            {!casesLoading && cases.length === 0 && <div className="case-empty">No cases yet. Add one above.</div>}
            {cases.map((c) => (
              <div key={c.id} className="case-item">
                <div className="case-item-header-row">
                  <span className="case-color-dot" style={{ background: c.color }} />
                  <div className="case-item-name">{c.name}</div>
                  <button className="case-delete-btn" onClick={() => deleteCase(c.id)} title="Delete project">✕</button>
                </div>
                <div className="case-item-meta">{c.created}</div>
                <div className="case-color-picker">
                  {['#2196F3', '#E53935', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1'].map(hex => (
                    <button
                      key={hex}
                      className={`case-color-swatch${c.color === hex ? ' case-color-swatch--active' : ''}`}
                      style={{ background: hex }}
                      onClick={() => updateCaseColor(c.id, hex)}
                      title={hex}
                    />
                  ))}
                </div>
                <button className="case-keydates-toggle" onClick={() => toggleExpandCase(c.id)}>
                  {expandedCaseId === c.id ? '▾' : '▸'} Key Dates {(projectEvents[c.id] || []).length > 0 ? `(${projectEvents[c.id].length})` : ''}
                </button>
                {expandedCaseId === c.id && (
                  <div className="case-keydates-body">
                    {(projectEvents[c.id] || []).map(ev => (
                      <div key={ev.id} className="case-keydate-row">
                        <span className="case-keydate-date">{ev.event_date}</span>
                        <span className="case-keydate-title">{ev.title}</span>
                        <button className="case-delete-btn" onClick={() => deleteProjectEvent(c.id, ev.id)} title="Delete date">✕</button>
                      </div>
                    ))}
                    <div className="case-keydate-add-row">
                      <input
                        type="date"
                        className="case-keydate-date-input"
                        value={newEventDate}
                        onChange={e => setNewEventDate(e.target.value)}
                      />
                      <input
                        type="text"
                        className="case-keydate-title-input"
                        placeholder="Title (e.g. Filing deadline)"
                        value={newEventTitle}
                        onChange={e => setNewEventTitle(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addProjectEvent(c.id, c.color)}
                      />
                      <button className="case-add-btn" onClick={() => addProjectEvent(c.id, c.color)}>Add</button>
                    </div>
                  </div>
                )}
                {currentConvId && (
                  <button
                    className="case-link-btn"
                    onClick={() => linkCaseToConv(c.id)}
                  >
                    + Link current chat
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Main layout ── */}
      <div className="main-layout">
        {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

        {/* ── Sidebar ── */}
        <div className={`sidebar ${sidebarOpen ? 'open' : ''}${sidebarCollapsed ? ' sidebar--collapsed' : ''}`} ref={sidebarRef}>

          {/* Collapsed rail — icon strip shown when sidebar is collapsed on desktop */}
          {sidebarCollapsed ? (
            <div className="sidebar-rail">
              <button className="sidebar-rail-btn" onClick={() => setSidebarCollapsed(false)} title="Expand sidebar">›</button>
              <button className="sidebar-rail-btn" onClick={async () => { setSidebarCollapsed(false); await refreshConversations(); startNewChat(); }} title="New chat">+</button>
            </div>
          ) : (
            <>

          <div className="sidebar-panes-scroll" style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Top pane -- conversations */}
          <div
            className="sidebar-pane sidebar-pane--conversations"
            style={{
              flex: convCollapsed ? '0 0 auto' : `0 0 ${convPct}%`,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <div className="sidebar-pane-header">
              <div className="sidebar-label">Conversations</div>
              <div style={{display:'flex',alignItems:'center',gap:'2px'}}>
              <button
                className="sidebar-collapse-btn"
                onClick={() => setSidebarCollapsed(true)}
                title="Collapse sidebar"
                style={{fontSize:'1rem',paddingBottom:'0.35rem'}}
              >‹</button>
              <button
                className="sidebar-collapse-btn"
                onClick={() => setConvCollapsed(!convCollapsed)}
                title={convCollapsed ? 'Expand' : 'Collapse'}
              >{convCollapsed ? '▸' : '▾'}</button>
              </div>
            </div>
            {!convCollapsed && (
              <>
                <button className="new-chat-btn" onClick={async () => { await refreshConversations(); startNewChat(); }}>
                  + New Chat
                </button>
                <div className="conv-list">
                  {conversations.map(c => (
                    <div
                      key={c.id}
                      className={`conv-item ${c.id === currentConvId ? 'active' : ''}`}
                      onClick={() => renamingConvId !== c.id && loadConversation(c.id)}
                      onDoubleClick={() => { setRenamingConvId(c.id); setRenameValue(c.title || ''); }}
                    >
                      {renamingConvId === c.id ? (
                        <input
                          className="conv-rename-input"
                          autoFocus
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onBlur={() => renameConversation(c.id, renameValue)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') renameConversation(c.id, renameValue);
                            if (e.key === 'Escape') setRenamingConvId(null);
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <>
                          <button
                            className="conv-rename-btn"
                            onClick={e => { e.stopPropagation(); setRenamingConvId(c.id); setRenameValue(c.title || ''); }}
                            title="Rename chat"
                          >✏️</button>
                          <span className="conv-item-title">{c.title || 'Untitled'}</span>
                          <button
                            className="conv-hide-btn"
                            onClick={e => { e.stopPropagation(); hideConversation(c.id); }}
                            title="Remove from history"
                          >×</button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Draggable Divider */}
          {!convCollapsed && (
            <div
              className="sidebar-divider sidebar-divider--draggable"
              onMouseDown={handleDividerMouseDown}
              title="Drag to resize"
            />
          )}

          {/* Bottom pane -- tabbed: Prompt History | Source Manifest */}
          <div
            className="sidebar-pane sidebar-pane--bottom"
            style={{
              flex: convCollapsed ? '1 1 auto' : `0 0 ${100 - convPct}%`,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <div className="bottom-panel-tabs">
              <button
                className={`bottom-tab ${bottomTab === 'prompts' ? 'bottom-tab--active' : ''}`}
                onClick={() => setBottomTab('prompts')}
              >
                Prompts
              </button>
              <button
                className={`bottom-tab ${bottomTab === 'manifest' ? 'bottom-tab--active' : ''}`}
                onClick={() => setBottomTab('manifest')}
              >
                Sources
              </button>
              <button
                className={`bottom-tab ${bottomTab === 'notes' ? 'bottom-tab--active' : ''}`}
                onClick={() => setBottomTab('notes')}
              >
                Notes {notes.length > 0 && <span style={{fontSize:'0.6rem',background:'#2196F3',color:'#fff',borderRadius:'8px',padding:'0 5px',marginLeft:'3px'}}>{notes.length}</span>}
              </button>
              <div className="tab-actions">
                {bottomTab === 'prompts' && <>
                  <button className="tab-action-btn" onClick={handleCopyPrompts} title="Copy prompts">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                  </button>
                  <button className="tab-action-btn" onClick={handlePrintPrompts} title="Print prompts">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  </button>
                  <button className="export-icon-btn" onClick={() => setExportMenuOpen(exportMenuOpen === 'prompts' ? null : 'prompts')} title="Export prompts">⬇</button>
                  {exportMenuOpen === 'prompts' && (
                    <ExportMenu
                      onExport={(fmt) => exportContent(fmt, buildPromptsText(), 'canjur-prompts', 'Canjur: Prompts')}
                      onClose={() => setExportMenuOpen(null)}
                    />
                  )}
                </>}
                {bottomTab === 'manifest' && <>
                  <button className="tab-action-btn" onClick={handleCopySources} title="Copy sources">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                  </button>
                  <button className="tab-action-btn" onClick={handlePrintSources} title="Print sources">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  </button>
                  <button className="export-icon-btn" onClick={() => setExportMenuOpen(exportMenuOpen === 'sources' ? null : 'sources')} title="Export sources">⬇</button>
                  {exportMenuOpen === 'sources' && (
                    <ExportMenu
                      onExport={(fmt) => exportContent(fmt, buildSourcesText(), 'canjur-sources', 'Canjur: Sources')}
                      onClose={() => setExportMenuOpen(null)}
                    />
                  )}
                </>}
                {bottomTab === 'notes' && <>
                  <button className="tab-action-btn" onClick={() => {
                    const w = window.open('', '_blank');
                    w.document.write('<html><head><title>Notes</title></head><body><pre style="font-family:serif;font-size:14px;line-height:1.8;padding:2rem">' +
                      notes.map((n,i) => `NOTE ${i+1}\n${n.text}\n${'─'.repeat(40)}`).join('\n\n') +
                    '</pre></body></html>'); w.print();
                  }} title="Print notes">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  </button>
                </>}
              </div>
            </div>

            <div className="bottom-panel-content">
              {/* Prompt History tab */}
              {bottomTab === 'prompts' && (
                <div className="prompt-list">
                  {promptHistory.length === 0 && (
                    <div className="prompt-empty">Prompts from this chat appear here.</div>
                  )}
                  {promptHistory.map((p, i) => (
                    <PromptItem
                      key={i}
                      prompt={p}
                      onUse={() => setInput(p)}
                    />
                  ))}
                </div>
              )}

              {/* Source Manifest tab */}
              {bottomTab === 'manifest' && (
                <div>
                  {!sourceManifest && (
                    <div className="manifest-empty">
                      Sources from this session appear here.
                    </div>
                  )}

                  {sourceManifest && sourceManifest.corpus.length > 0 && (
                    <>
                      {sourceManifest.corpus.filter(item => {
                        const t = typeof item === 'object' ? item.title : item;
                        return t && t.replace(/[•\-\s*]/g,'').length > 2;
                      }).map((item, i) => {
                        const label = typeof item === 'object'
                          ? item.title
                          : item.replace(/\*\(score:[^)]+\)\*/gi,'').replace(/score[:\s]+[\d.]+/gi,'').replace(/^[•-]\s*/,'').replace(/\*/g,'').trim();
                        return label ? (
                          <div key={i} className="manifest-item">
                            <span className="manifest-check">✓</span>
                            <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{label}</span>
                          </div>
                        ) : null;
                      })}
                    </>
                  )}

                  {sourceManifest && sourceManifest.live.length > 0 && (
                    <>
                      <div className="manifest-section-label">Live</div>
                      {sourceManifest.live.map((item, i) => (
                        <div key={i} className="manifest-item manifest-item--live">
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeof item === 'object' ? item.title : item}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Notes tab */}
              {bottomTab === 'notes' && (
                <div className="notes-list">
                  {notes.length === 0 && (
                    <div className="prompt-empty">Right-click any paragraph in a chat response to add a note.</div>
                  )}
                  {notes.map((note, idx) => (
                    <div key={note.id || idx} className="note-item">
                      <div className="note-item-text" onClick={() => openNoteEditor(note.msg_index, note.block_id, note)}>
                        {note.text.slice(0, 80)}{note.text.length > 80 ? '...' : ''}
                      </div>
                      <div className="note-item-actions">
                        <button className="tab-action-btn" title="Jump to section" onClick={() => { jumpToBlock(note.block_id); setBottomTab('notes'); }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                          </svg>
                        </button>
                        <button className="tab-action-btn" title="Delete note" onClick={() => deleteNote(note.id)}
                          style={{color:'#c0392b'}}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          </div>

          <SidebarBottomActions
            onNewChat={async () => { await refreshConversations(); startNewChat(); setSidebarOpen(false); }}
            onOpenProjects={() => { setCaseTrackerOpen(!caseTrackerOpen); setSidebarOpen(false); }}
            onToggleCouncil={() => { if (councilMembers.length >= 2 && councilPrime) setCouncilMode(m => !m); setSidebarOpen(false); }}
            councilDisabled={councilMembers.length < 2 || !councilPrime}
            onOpenCode={() => { setCodePanelOpen(!codePanelOpen); setSidebarOpen(false); }}
            onOpenSettings={() => { setSettingsOpen(!settingsOpen); setCaseTrackerOpen(false); setSidebarOpen(false); }}
            onOpenHelp={() => { setHelpOpen(!helpOpen); setSidebarOpen(false); }}
            onOpenAccount={() => { setSettingsOpen(true); setCaseTrackerOpen(false); setSidebarOpen(false); }}
            onOpenPromptOptimizer={() => { setPromptOptimizerOpen(!promptOptimizerOpen); setSidebarOpen(false); }}
          />

            </>
          )}
        </div>

        {/* ── Chat area ── */}
        <div className="chat-area">
          <div className="messages" onScroll={(e) => { const el = e.currentTarget; atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60; }}>
            {messages.map((m, i) => (
              <div key={i} className={`msg ${m.role}`}>
                <div className="msg-role">
                  {m.role === 'user'
                    ? 'You'
                    : m.role === 'system-display'
                    ? 'System'
                    : (m.modelLabel ? `${m.modelLabel} · ${m.modelSub}` : selectedModel.label)
                  }
                  {m.role === 'assistant' && m.jurisdictions && m.jurisdictions.length > 0 && (
                    <span className="jurisdiction-flags">
                      {m.jurisdictions.map((j, ji) => (
                        <span key={ji} className="jurisdiction-tag">{j}</span>
                      ))}
                    </span>
                  )}
                </div>
                {m.thinking ? (
                  <div className="thinking"><span></span><span></span><span></span></div>
                ) : (
                  renderContent(m.content, i, m.streaming)
                )}
                {m.failed && m.retry && (
                  <button
                    className="retry-btn"
                    onClick={() => retryMessage(m)}
                    disabled={loading}
                    title="Send the same request again"
                  >↻ Retry</button>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* ── Input area ── */}
          <div className="input-area">
            {attachedDocs.map(doc => (
              <div className="attached-doc" key={doc.name}>
                <span className="attached-doc-name">{doc.name}</span>
                <button className="attached-doc-remove" onClick={() => setAttachedDocs(prev => prev.filter(d => d.name !== doc.name))}>✕</button>
              </div>
            ))}
            <div className="input-row">
              <button className="attach-btn" onClick={() => fileInputRef.current.click()} title="Attach document">+</button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.txt,.md"
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleTextareaInput}
                onKeyDown={handleKeyDown}
                placeholder="Ask a legal research question..."
                rows={1}
              />
              {loading ? (
                <button
                  className="send-btn send-btn--stop"
                  onClick={handleStopStream}
                  title="Stop response"
                >
                  Stop
                </button>
              ) : (
                <button
                  className="send-btn"
                  onClick={() => sendQuery()}
                  disabled={!input.trim()}
                >
                  Send
                </button>
              )}
            </div>
            <div className="input-hint">Enter to send &nbsp;&middot;&nbsp; Shift+Enter for new line &nbsp;&middot;&nbsp; Drag &amp; drop a document to attach</div>
          </div>
        </div>
      </div>

      {/* Context menu */}
      {contextMenu && (
        <>
          <div style={{position:'fixed',inset:0,zIndex:9998}} onClick={() => setContextMenu(null)} />
          <div style={{
            position:'fixed', zIndex:9999,
            left:contextMenu.x, top:contextMenu.y,
            background:'#fff', border:'0.5px solid #D0DCE8',
            borderRadius:'6px', boxShadow:'0 4px 16px rgba(0,0,0,0.12)',
            padding:'4px 0', minWidth:'140px', fontSize:'0.8rem'
          }}>
            <div style={{padding:'8px 14px',cursor:'pointer',color:'#1A2A3A'}}
              onMouseOver={e=>e.currentTarget.style.background='#EBF1FA'}
              onMouseOut={e=>e.currentTarget.style.background='transparent'}
              onClick={()=>{
                const el = document.getElementById(contextMenu.blockId);
                const text = contextMenu.selectedText || (el ? el.innerText : '');
                if (text) navigator.clipboard.writeText(text);
                setContextMenu(null);
              }}>
              📋 {contextMenu.selectedText ? 'Copy selection' : 'Copy text'}
            </div>
            <div style={{padding:'8px 14px',cursor:'pointer',color:'#1A2A3A'}}
              onMouseOver={e=>e.currentTarget.style.background='#EBF1FA'}
              onMouseOut={e=>e.currentTarget.style.background='transparent'}
              onClick={()=>openNoteEditor(contextMenu.msgIndex,contextMenu.blockId,notes.find(n=>n.block_id===contextMenu.blockId))}>
              📌 {notes.find(n=>n.block_id===contextMenu.blockId)?'Edit note':'Add note'}
            </div>
            {notes.find(n=>n.block_id===contextMenu.blockId)&&(
              <div style={{padding:'8px 14px',cursor:'pointer',color:'#c0392b'}}
                onMouseOver={e=>e.currentTarget.style.background='#FEF0F0'}
                onMouseOut={e=>e.currentTarget.style.background='transparent'}
                onClick={()=>{deleteNote(notes.find(n=>n.block_id===contextMenu.blockId).id);setContextMenu(null);}}>
                🗑 Delete note
              </div>
            )}
          </div>
        </>
      )}

      {/* Note editor bubble */}
      {activeNote && (
        <>
          <div style={{position:'fixed',inset:0,zIndex:9997}} onClick={closeNoteEditor} />
          <div style={{
            position:'fixed', zIndex:9998,
            left:Math.min(activeNote.x, window.innerWidth-320),
            top:Math.min(activeNote.y, window.innerHeight-200),
            width:'300px', background:'#FFFDF0',
            border:'1px solid #D0DCE8', borderRadius:'8px',
            boxShadow:'0 4px 20px rgba(0,0,0,0.15)', padding:'10px'
          }} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:'0.65rem',color:'#8A9BB0',marginBottom:'6px',letterSpacing:'0.1em',textTransform:'uppercase'}}>Note</div>
            <textarea autoFocus value={activeNote.text}
              onChange={e=>setActiveNote(prev=>({...prev,text:e.target.value}))}
              style={{width:'100%',minHeight:'80px',border:'none',background:'transparent',
                resize:'vertical',fontSize:'0.82rem',lineHeight:1.6,color:'#1A2A3A',
                outline:'none',fontFamily:'inherit'}}
              placeholder="Type your note..." />
            <div style={{display:'flex',justifyContent:'flex-end',gap:'6px',marginTop:'6px'}}>
              <button onClick={closeNoteEditor}
                style={{fontSize:'0.7rem',padding:'3px 10px',background:'#2196F3',color:'#fff',border:'none',borderRadius:'4px',cursor:'pointer'}}>
                Save
              </button>
              <button onClick={()=>setActiveNote(null)}
                style={{fontSize:'0.7rem',padding:'3px 10px',background:'transparent',color:'#8A9BB0',border:'0.5px solid #D0DCE8',borderRadius:'4px',cursor:'pointer'}}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
